/* eslint-disable react-hooks/rules-of-hooks */
import React, { useRef, useState, Fragment, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Fade,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputLabel,
  Link,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  darken,
  lighten,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  AddOutlined,
  AddPhotoAlternateOutlined,
  AutoAwesome,
  Campaign,
  CloseTwoTone,
  Delete,
  InfoOutlined,
  Lock,
  Notifications,
  SendOutlined as SendOutlinedIcon,
  SmartToyOutlined as SmartToyOutlinedIcon,
  MoreVert,
  UploadFileOutlined,
  VideoFileOutlined,
} from '@mui/icons-material';
import { IconInfoCircle } from '@tabler/icons-react';
import ContextualInfoPopover from 'ui-component/help/ContextualInfoPopover';
import useLocalization from 'hooks/useLocalization';
import { useGeminiConnection } from 'hooks/useGeminiConnection';
import {
  WhatsappEditTemplate,
  WhatsappOriginalTemplateButtons,
  WhatsappOriginalTemplateComponent,
  WhatsappOriginalTemplateHeader,
  WhatsappTemplateButtonsType,
  WhatsappTemplateCategory,
  WhatsappTemplateComponentType,
  WhatsappTemplateHeaderFormat,
} from 'types/templates';
import { useNavigate, useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from 'notistack';
import { dispatch, store, useSelector } from 'store';
import MainCard from 'ui-component/cards/MainCard';
import { DASHBOARD_PATH } from 'config';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import BreadCrumbsHomeIcon from 'ui-component/extended/BreadcrumbsHomeIcon';
import { gridSpacing } from 'store/constant';
import TemplatePreviewSidebar from '../list/TemplatePreviewSidebar';
import Loader from 'ui-component/Loader';
import ViewRevealer from 'ui-component/system/ViewRevealer';
import { languages } from 'utils/languages';
import { generateUniqueId } from 'utils/helpers';
import {
  buildDuplicateSessionBaseline,
  serializeTemplateForDuplicateCompare,
} from 'utils/template-duplicate-prefill';
import { varEndRegular, varsRegular, internationalPhoneRegex } from 'utils/regexes';
import { VisuallyHiddenInput } from 'ui-component/system/VisuallyHiddenInput';
import { fetcher } from 'utils/axios';
import { getWhatsappTemplates, handleErrorPopup } from 'store/slices/templates';
import RightAppSidebarContent from 'layout/MainLayout/RightAppSidebarContent';
import { closeRightSidebar, openRightSidebar, setRightSidebar } from 'store/slices/dual-sidebar';
import Avatar from 'ui-component/extended/Avatar';
import ColoredLetterAvatar from 'ui-component/display/ColoredLetterAvatar';
import DotsLoader from 'ui-component/loaders/DotsLoader';
import MarkdownRenderer from 'ui-component/system/MarkdownRenderer';
import { ThemeMode } from 'config';
import { createUpdateTemplateDraft, fetchGetDraftTemplateById } from 'api/templates';
import { getFacebookError } from 'utils/facebook';
import {
  buildExistingTemplatesForAiPrompt,
  createLegacyTemplateGPTSession,
  generateManualTemplateMetadataAI,
  generateSmartTemplateAI,
  generateTemplateLegacyGPT,
  MANUAL_METADATA_PLACEHOLDER_NAME_EN,
  serializeTemplateDraftForMetadataAi,
} from './ai/AIGeneratorServices';
import { AI_MARKETING_OPT_OUT_QUICK_REPLY_TEXT, type SmartTemplateAiPayload } from './ai/smartTemplateAiTypes';
import SmartTemplateBriefDialog from './ai/SmartTemplateBriefDialog';
import TemplateAIGeneratedPreviewDialog from './ai/TemplateAIGeneratedPreviewDialog';
import { useApplyGeneratedTemplateToForm } from './ai/useApplyGeneratedTemplateToForm';
import DefaultHeaderFileSection from './DefaultHeaderFileSection';

let nextVariableIndexBody = 1;
let prevValue = '';

/** טקסט כפתור הסרה אוטומטי לקטגוריית שיווק — Quick Reply (תואם WhatsApp Cloud API) */
const MARKETING_OPT_OUT_BUTTON_TEXT = 'הסר';

const MARKETING_OPT_OUT_INFO_TEXT =
  'שימו לב: עבור הודעות בעלות תוכן שיווקי, הכללת כפתור הסרה (Opt-out) היא חובה על פי תקנות פייסבוק.\n\n' +
  'המלצה: גם אם התוכן הנוכחי אינו שיווקי ישירות, מומלץ להשאיר את כפתור ההסרה בקטגוריית שיווק. הדבר משפיע לטובה על דירוג איכות ההודעה בפייסבוק (Message Quality Score) ומונע חסימות עתידיות.';

const MARKETING_OPT_OUT_SPOTLIGHT_TITLE = 'הוספנו עבורך כפתור הסרה';

const MARKETING_OPT_OUT_SPOTLIGHT_BODY =
  'לתבניות בקטגוריית שיווק, מטא דורשת כפתור הסרה (Opt-out) כדי לאפשר ללקוח לבטל קבלת תוכן שיווקי. הכפתור נוסף אוטומטית כ"מענה מהיר" עם הטקסט "הסר" — תוכל לערוך את הניסוח או להשאיר כפי שהוא.';

/** ניווט פנימי — תואם ל־MainRoutes: broadcasts / unsubscribe-settings */
const BROADCAST_UNSUBSCRIBE_SETTINGS_PATH = '/broadcasts/unsubscribe-settings';

const LS_MARKETING_OPT_OUT_SPOTLIGHT_SUPPRESS = 'berry-template-marketing-opt-out-spotlight-suppress';

function readMarketingOptOutSpotlightSuppress(): boolean {
  try {
    return localStorage.getItem(LS_MARKETING_OPT_OUT_SPOTLIGHT_SUPPRESS) === '1';
  } catch {
    return false;
  }
}

function writeMarketingOptOutSpotlightSuppress(): void {
  try {
    localStorage.setItem(LS_MARKETING_OPT_OUT_SPOTLIGHT_SUPPRESS, '1');
  } catch {
    /* ignore quota / private mode */
  }
}

type MarketingOptOutSpotlightRect = { top: number; left: number; width: number; height: number };

/**
 * מלבן spotlight רק על שדה הטקסט של כפתור ההסרה (לא כל השורה) — מונע חפיפה עם הפופאפ והתרחבות ויזואלית.
 */
function getSpotlightRectForOptOutField(el: HTMLElement): DOMRect {
  const anchor = el.querySelector('[data-marketing-opt-out-field="true"]') as HTMLElement | null;
  const inputRoot = anchor?.querySelector('.MuiOutlinedInput-root') as HTMLElement | null;
  if (inputRoot) return inputRoot.getBoundingClientRect();
  const fallback = el.querySelector('.MuiOutlinedInput-root') as HTMLElement | null;
  if (fallback) return fallback.getBoundingClientRect();
  return el.getBoundingClientRect();
}

/** מרחיב את מלבן ה־viewport בצורה סימטרית ומבצע קלמפינג לגבולות החלון */
function padViewportRect(r: DOMRect, pad: number): MarketingOptOutSpotlightRect {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = r.left - pad;
  let top = r.top - pad;
  let width = r.width + 2 * pad;
  let height = r.height + 2 * pad;
  if (left < 0) {
    width += left;
    left = 0;
  }
  if (top < 0) {
    height += top;
    top = 0;
  }
  if (left + width > vw) width = Math.max(0, vw - left);
  if (top + height > vh) height = Math.max(0, vh - top);
  return {
    top: Math.round(top),
    left: Math.round(left),
    width: Math.round(width),
    height: Math.round(height),
  };
}

const SPOTLIGHT_DIM = 'rgba(12, 14, 28, 0.58)';

/** רקע מוחשך עם חור מעל שורת הכפתור + הודעה ב־portal */
function MarketingOptOutSpotlightOverlay({
  rect,
  open,
  onDismiss,
}: {
  rect: MarketingOptOutSpotlightRect | null;
  open: boolean;
  /** true = שמירה ב-localStorage שלא להציג שוב את ה-spotlight */
  onDismiss: (suppressFuture?: boolean) => void;
}) {
  const theme = useTheme();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  React.useEffect(() => {
    if (open) setDontShowAgain(false);
  }, [open]);

  if (!open || !rect) return null;

  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
  const { top, left, width, height } = rect;
  const bottomHole = top + height;
  const rightHole = left + width;

  const handleDimClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss(false);
  };

  const messageBelow = bottomHole + 200 < vh;

  const dimSx = {
    bgcolor: SPOTLIGHT_DIM,
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    transition: 'opacity 0.35s ease',
  };

  return createPortal(
    <Fade in={open} timeout={380}>
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: theme.zIndex.modal,
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ pointerEvents: 'auto' }}>
          <Box
            onClick={handleDimClick}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: top,
              ...dimSx,
            }}
          />
          <Box
            onClick={handleDimClick}
            sx={{
              position: 'fixed',
              top: bottomHole,
              left: 0,
              right: 0,
              bottom: 0,
              ...dimSx,
            }}
          />
          <Box
            onClick={handleDimClick}
            sx={{
              position: 'fixed',
              top,
              left: 0,
              width: left,
              height,
              ...dimSx,
            }}
          />
          <Box
            onClick={handleDimClick}
            sx={{
              position: 'fixed',
              top,
              left: rightHole,
              right: 0,
              height,
              ...dimSx,
            }}
          />
        </Box>

        {/* מרכוז ב-flex: בלי left:50%/translateX — ב-RTL stylis הופך את זה ומזיז את המודאל */}
        <Box
          sx={{
            position: 'fixed',
            insetInline: 0,
            zIndex: 1,
            display: 'flex',
            justifyContent: 'center',
            paddingInline: 2,
            pointerEvents: 'none',
            boxSizing: 'border-box',
            ...(messageBelow
              ? { top: bottomHole + 20 }
              : { top: top - 16, transform: 'translateY(-100%)' }),
          }}
        >
          <Paper
            elevation={12}
            sx={{
              maxWidth: 440,
              width: 'min(440px, calc(100vw - 32px))',
              p: 2.5,
              pointerEvents: 'auto',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: theme.shadows[16],
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {MARKETING_OPT_OUT_SPOTLIGHT_TITLE}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: 1.5 }}>
              {MARKETING_OPT_OUT_SPOTLIGHT_BODY}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: 2 }}>
              ניתן לנהל את{' '}
              <Link
                component={RouterLink}
                to={BROADCAST_UNSUBSCRIBE_SETTINGS_PATH}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 600 }}
              >
                הגדרות ההסרה והביטול
              </Link>
              {' '}
              במסך השידורים (נפתח בלשונית חדשה).
            </Typography>
            <FormControlLabel
              sx={{
                mt: 0.5,
                mb: 1.5,
                alignItems: 'center',
                marginInline: 0,
                minHeight: 40,
                '& .MuiFormControlLabel-label': { lineHeight: 1.3 },
              }}
              control={
                <Checkbox
                  checked={dontShowAgain}
                  onChange={() => setDontShowAgain((v) => !v)}
                  color="primary"
                  size="small"
                  sx={{ py: 0.5 }}
                />
              }
              label={<Typography variant="body2">אל תציג שוב</Typography>}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => onDismiss(dontShowAgain)}
              sx={{ borderRadius: 1.5 }}
            >
              הבנתי, תודה
            </Button>
          </Paper>
        </Box>
      </Box>
    </Fade>,
    document.body,
  );
}

/** Popover + Tooltip ליד כפתור ההסרה האוטומטי */
function MarketingOptOutInfoButton() {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const popoverHorizontal: 'left' | 'right' = theme.direction === 'rtl' ? 'right' : 'left';

  return (
    <>
      <Tooltip
        title={MARKETING_OPT_OUT_INFO_TEXT}
        placement="left"
        enterTouchDelay={0}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 400,
              p: 2,
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: theme.shadows[8],
              whiteSpace: 'pre-line',
              fontSize: '0.875rem',
              lineHeight: 1.65,
            },
          },
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="מידע על כפתור הסרה לשיווק"
          aria-haspopup="true"
          aria-expanded={open}
          sx={{
            p: 0.35,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          }}
        >
          <IconInfoCircle size={20} stroke={1.5} />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: popoverHorizontal }}
        transformOrigin={{ vertical: 'top', horizontal: popoverHorizontal }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              maxWidth: 400,
              bgcolor: 'background.paper',
              boxShadow: theme.shadows[8],
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            },
          },
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, whiteSpace: 'pre-line' }}>
          {MARKETING_OPT_OUT_INFO_TEXT}
        </Typography>
      </Popover>
    </>
  );
}

/** ממזג example.header_handle מפורמטים שונים מהשרת (camelCase / PascalCase) */
function normalizeTemplateForEditor(template: WhatsappEditTemplate): WhatsappEditTemplate {
  return {
    ...template,
    components: template.components.map((comp) => {
      if (comp.type !== 'HEADER' || !comp.example) return comp;
      const ex = comp.example as Record<string, unknown>;
      const handle =
        (ex.header_handle as string | undefined) ??
        (ex.HeaderHandle as string | undefined) ??
        (ex.headerHandle as string | undefined);
      if (handle === undefined) return comp;
      return {
        ...comp,
        example: { ...ex, header_handle: handle },
      };
    }),
  };
}

function headerHasMediaHandle(header: WhatsappOriginalTemplateHeader | undefined): boolean {
  if (!header) return false;
  const ex = header.example as Record<string, unknown> | undefined;
  if (!ex) return false;
  const raw =
    (ex.header_handle as string | undefined) ??
    (ex.HeaderHandle as string | undefined) ??
    (ex.headerHandle as string | undefined);
  return raw != null && String(raw).trim() !== '';
}

// Phone number formatting function for WhatsApp templates
const formatPhoneNumberForWhatsApp = (phoneNumber: string): string => {
  if (!phoneNumber) return phoneNumber;

  // Remove all non-digit characters except +
  let cleaned = phoneNumber.replace(/[^\d+]/g, '');

  // If it starts with 00, replace with +
  if (cleaned.startsWith('00')) {
    cleaned = '+' + cleaned.substring(2);
  }

  // If it doesn't start with + and is Israeli number (starts with 05), add +972
  if (!cleaned.startsWith('+')) {
    if (cleaned.startsWith('05')) {
      // Israeli mobile number: 05X-XXXXXXX -> +972-5X-XXXXXXX
      cleaned = '+972' + cleaned.substring(1);
    } else if (cleaned.startsWith('5') && cleaned.length === 9) {
      // Israeli mobile without leading 0: 5X-XXXXXXX -> +972-5X-XXXXXXX
      cleaned = '+972' + cleaned;
    } else {
      // For other numbers, assume they need country code
      // You might want to add more country-specific logic here
      cleaned = '+' + cleaned;
    }
  }

  // Validate final format (E.164: +[country code][number], max 15 digits total)
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  if (e164Regex.test(cleaned)) {
    return cleaned;
  }

  // If validation fails, return original (let API handle the error)
  return phoneNumber;
};

type TemplateAIHelperProps = {
  /** Fires when the server returns a non-loading assistant message (for preview + inject). */
  onAssistantMessageComplete?: (text: string) => void;
};

// AI Helper Component (Legacy server chat — see AIGeneratorServices)
const TemplateAIHelper: React.FC<TemplateAIHelperProps> = ({ onAssistantMessageComplete }) => {
  const [inputValue, setInputValue] = React.useState<string>('');
  const [messages, setMessages] = React.useState<
    Array<{ id: string; sender: 'user' | 'ai'; message: string }>
  >([]);
  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = React.useState<boolean>(false);

  const messagesRef = React.useRef<Array<{ id: string; sender: 'user' | 'ai'; message: string }>>([]);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);

  // Get user data for avatar
  const { Image, FirstName, LastName } = useSelector((state) => state.user || {});
  const userName = `${FirstName || ''} ${LastName || ''}`.trim() || 'אתה';

  const avatarSx = {
    width: 30,
    height: 30,
    fontSize: 16,
  };

  const handleMessages = (newMessages: Array<{ id: string; sender: 'user' | 'ai'; message: string }>) => {
    setMessages(newMessages);
    messagesRef.current = newMessages;
  };

  const handleAddMessageToList = (message: { id: string; sender: 'user' | 'ai'; message: string }) => {
    const newMessages = JSON.parse(JSON.stringify(messagesRef.current));

    const existingMessageIndex = newMessages.findIndex((msg: any) => msg.id === message.id);
    if (existingMessageIndex !== -1) {
      newMessages[existingMessageIndex] = message;
    } else {
      newMessages.push(message);
    }

    handleMessages(newMessages);
    setTimeout(() => {
      handleScrollBottom();
    }, 50);
  };

  const handleScrollBottom = () => {
    messagesContainerRef.current?.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  // Start AI session when component mounts
  const startTemplateAISession = React.useCallback(async () => {
    try {
      setSessionLoading(true);

      const thread = await createLegacyTemplateGPTSession();
      if (thread) setSessionId(thread);
    } catch (error) {
      console.log('startTemplateAISession error: ', error);
    } finally {
      setSessionLoading(false);
    }
  }, []);

  const sendMessageToAI = async (directValue: string | null = null) => {
    try {
      const currentValue = directValue ?? inputValue;
      if (!directValue) setInputValue('');

      const newMessageObject = {
        id: generateUniqueId(
          messagesRef.current.map((msg) => msg.id),
          5,
        ),
        sender: 'user' as const,
        message: currentValue,
      };
      handleAddMessageToList(newMessageObject);

      const newAILoadingResponse = {
        id: generateUniqueId(
          messagesRef.current.map((msg) => msg.id),
          5,
        ),
        sender: 'ai' as const,
        message: 'loading',
      };
      setTimeout(() => {
        handleAddMessageToList(newAILoadingResponse);
      }, 100);

      if (!sessionId) return;
      const aiAnswer = await generateTemplateLegacyGPT(sessionId, currentValue);
      if (aiAnswer) {
        newAILoadingResponse.message = aiAnswer;
        handleAddMessageToList(newAILoadingResponse);
        onAssistantMessageComplete?.(aiAnswer);
      }
    } catch (error) {
      console.warn('sendMessageToAI error: ', error);
      const errorMessage = {
        id: generateUniqueId(
          messagesRef.current.map((msg) => msg.id),
          5,
        ),
        sender: 'ai' as const,
        message: 'מצטער, אירעה שגיאה בחיבור לשרת. נסה שוב מאוחר יותר.',
      };
      handleAddMessageToList(errorMessage);
    }
  };

  const handleSendMessageOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      sendMessageToAI();
    }
  };

  React.useEffect(() => {
    startTemplateAISession();
  }, [startTemplateAISession]);

  React.useEffect(() => {
    // Scroll to bottom when new messages are added
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <Box sx={{ height: '100%' }}>
      {sessionLoading ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <CircularProgress size={24} color="secondary" />
          <Typography>מתחיל שיחה עם ה-AI...</Typography>
        </Box>
      ) : sessionId ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box
            sx={{
              width: '100%',
              flex: '1 1 auto',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              mr: -1.25,
              pr: 1.25,
              py: 1.25,
              gap: 1.25,
            }}
            ref={messagesContainerRef}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: 1.5,
                  flexDirection: message.sender === 'user' ? 'row' : 'row-reverse',
                }}
              >
                <Box>
                  {message.sender === 'user' ? (
                    Image ? (
                      <Avatar src={Image} alt={userName} sx={avatarSx} />
                    ) : (
                      <ColoredLetterAvatar name={userName} sx={avatarSx} />
                    )
                  ) : (
                    <Avatar sx={avatarSx}>
                      <SmartToyOutlinedIcon />
                    </Avatar>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: message.sender === 'user' ? 'row' : 'row-reverse',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: 400,
                      display: 'inline-flex',
                      py: 1,
                      px: 1.25,
                      borderRadius: 1,
                      bgcolor: (theme) =>
                        theme.palette.mode === ThemeMode.DARK
                          ? alpha(
                              darken(
                                message.sender === 'user'
                                  ? theme.palette.primary.main
                                  : theme.palette.secondary.main,
                                0.5,
                              ),
                              0.65,
                            )
                          : lighten(
                              message.sender === 'user'
                                ? theme.palette.primary.main
                                : theme.palette.secondary.main,
                              0.9,
                            ),
                    }}
                  >
                    {message.sender === 'ai' && message.message === 'loading' ? (
                      <DotsLoader size={20} />
                    ) : message.sender === 'ai' ? (
                      <MarkdownRenderer markdownText={message.message} />
                    ) : (
                      <Typography>{message.message}</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
            {messages.length === 0 && (
              <Typography
                textAlign={'center'}
                sx={{
                  whiteSpace: 'pre-line',
                  color: 'text.secondary',
                }}
              >
                שאל את ה-AI כל שאלה על יצירת תבניות...
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              mx: -2.5,
              px: 2.5,
              pb: 1.75,
              pt: 1.25,
            }}
          >
            <Grid container spacing={1}>
              <Grid size="grow">
                <TextField
                  fullWidth
                  size="small"
                  label="שאל את ה-AI..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleSendMessageOnEnter}
                  disabled={!sessionId}
                  multiline={false}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '42px',
                      minHeight: '42px',
                      maxHeight: '42px',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                    },
                    '& .MuiInputBase-input': {
                      padding: '0 14px !important',
                      lineHeight: '42px',
                      height: '42px',
                      resize: 'none',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      '&::placeholder': {
                        lineHeight: '42px',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size="auto">
                <Button
                  variant="contained"
                  sx={{
                    height: 42,
                    width: 75,
                  }}
                  onClick={() => sendMessageToAI()}
                  disabled={!inputValue || !sessionId}
                >
                  <SendOutlinedIcon />
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography>שגיאה בהתחברות ל-AI. נסה שוב מאוחר יותר.</Typography>
        </Box>
      )}
    </Box>
  );
};

type TemplateErrorsType = {
  name: boolean;
  nameInvalidChars?: boolean;
  nameInvalidStartEnd?: boolean;
  nameConsecutiveUnderscores?: boolean;
  niceName: boolean;
  category: boolean;
  bodyText: boolean;
  headerText?: boolean;
  headerMedia?: boolean;
  headerTextParam?: boolean;
  footerText?: boolean;
  bodyParams?: number[];
  footerParams?: number[];
  buttons?: any[];
  bodyVarsErrorType?: string;
};

function CreateEditTemplate({
  templateType = 'whatsapp',
}: {
  templateType?: 'whatsapp' | 'draft' | 'predefined';
}) {
  const { currentLocale, currentLanguage } = useLocalization();
  const localization = React.useMemo(() => currentLocale.settingsPopup.templates.createNew, [currentLocale]);

  // Media query for mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Default template structure for resetting the form
  const getDefaultTemplate = (): WhatsappEditTemplate => ({
    id: null,
    name: '',
    niceName: '',
    language: 'he',
    category: undefined,
    components: [
      {
        type: 'BODY',
        text: '',
        format: null,
        buttons: null,
      },
    ],
  });

  // Reset form to default state
  const resetTemplateForm = () => {
    setSelectedTemplate(getDefaultTemplate());
    setCreateTemplateErrors({
      name: false,
      nameInvalidChars: false,
      nameInvalidStartEnd: false,
      nameConsecutiveUnderscores: false,
      niceName: false,
      category: false,
      bodyText: false,
      headerText: false,
      headerMedia: false,
      headerTextParam: false,
      footerText: false,
      bodyParams: [],
      buttons: [],
    });
    // Reset other form-related state
    nextVariableIndexBody = 1;
    prevValue = '';
  };
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const appliedCatalogFromStateRef = React.useRef(false);
  const duplicateBaselineRef = React.useRef<string | null>(null);
  const [isDuplicateSession, setIsDuplicateSession] = React.useState(false);
  const [duplicateSourceLabel, setDuplicateSourceLabel] = React.useState('');
  const { whatsappTemplates, whatsappOriginalTemplates } = useSelector((state) => state.templates);
  const company = useSelector((state) => state.company);
  const businessAccountID = React.useMemo(() => company?.BusinessAccountID, [company]);

  // Filter languages to only include the requested ones
  const allowedLanguages = React.useMemo(() => {
    const codes = ['he', 'en', 'ar', 'ru', 'es'];
    const result = [];
    for (const code of codes) {
      const lang = languages.find((lang) => lang.code === code);
      if (lang) result.push(lang);
    }
    return result;
  }, []);

  // Get localized category and language texts
  const getCategoryText = React.useCallback(
    (category: string) => {
      if (currentLanguage === 'he') {
        switch (category) {
          case 'MARKETING':
            return 'שיווק';
          case 'UTILITY':
            return 'שירות';
          case 'AUTHENTICATION':
            return 'אימות';
          default:
            return category;
        }
      }
      // English fallback
      switch (category) {
        case 'MARKETING':
          return 'Marketing';
        case 'UTILITY':
          return 'Utility';
        case 'AUTHENTICATION':
          return 'Authentication';
        default:
          return category;
      }
    },
    [currentLanguage],
  );

  const getLanguageText = React.useCallback(
    (langCode: string, langName: string) => {
      if (currentLanguage === 'he') {
        switch (langCode) {
          case 'he':
            return 'עברית';
          case 'en':
            return 'אנגלית';
          case 'es':
            return 'ספרדית';
          case 'ar':
            return 'ערבית';
          case 'ru':
            return 'רוסית';
          default:
            return langName;
        }
      }
      return langName; // Default to original language name
    },
    [currentLanguage],
  );

  const isEdit: boolean = React.useMemo(() => !!id, [id]);
  const gemini = useGeminiConnection();
  const geminiNoConnectionTooltip = localization.manualMetaAi.noConnectionTooltip;

  const [submittingTemplate, setSubmittingTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsappEditTemplate>({
    id: null,
    name: '',
    niceName: '',
    language: 'he',
    category: undefined,
    components: [
      {
        type: 'BODY',
        text: '',
        format: null,
        buttons: null,
      },
    ],
  });
  const [createTemplateErrors, setCreateTemplateErrors] = useState<TemplateErrorsType>({
    name: false,
    niceName: false,
    category: false,
    bodyText: false,
    headerText: false,
    headerMedia: false,
    headerTextParam: false,
    footerText: false,
    bodyParams: [],
    buttons: [],
  });

  const duplicateMeetsChangeRequirement = React.useMemo(() => {
    if (!isDuplicateSession || duplicateBaselineRef.current == null) return true;
    return serializeTemplateForDuplicateCompare(selectedTemplate) !== duplicateBaselineRef.current;
  }, [isDuplicateSession, selectedTemplate]);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const selectedDraftIdRef = React.useRef<number | null>(null);

  const mediaHeaderTooltips: {
    [key in WhatsappTemplateHeaderFormat]?: string;
  } = {
    IMAGE: localization.header.typeMedia.image.infoTooltip,
    VIDEO: localization.header.typeMedia.video.infoTooltip,
    DOCUMENT: localization.header.typeMedia.document.infoTooltip,
  };

  const errorTexts = {
    textInput: localization.errorTexts.textInput,
    param: localization.errorTexts.param,
    file: localization.errorTexts.file,
    headerMediaRequired:
      (localization.errorTexts as { headerMediaRequired?: string }).headerMediaRequired ?? localization.errorTexts.file,
  };

  const [createNewTemplateFormStates, setCreateNewTemplateFormStates] = useState<{
    showCategories: boolean;
    showLanguages: boolean;
    showHeaderTypeSelection: boolean;
    headerType: 'text' | 'media';
    flowType: 'flow_id' | 'flow_json';
  }>({
    showCategories: false,
    showLanguages: false,
    showHeaderTypeSelection: false,
    headerType: 'text',
    flowType: 'flow_id',
  });

  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [bodyVarsError, setBodyVarsError] = useState(false);
  const [buttonMenuAnchor, setButtonMenuAnchor] = useState<null | HTMLElement>(null);
  /** מזהה כפתור "הסר" שנוסף אוטומטית — לצורך אייקון מידע */
  const [marketingOptOutTrackedButtonId, setMarketingOptOutTrackedButtonId] = useState<string | null>(null);
  /** Spotlight: מזהה שורה + מיקום לרקע מוחשך והודעה */
  const [marketingOptOutSpotlightButtonId, setMarketingOptOutSpotlightButtonId] = useState<string | null>(null);
  const [marketingOptOutSpotlightRect, setMarketingOptOutSpotlightRect] =
    useState<MarketingOptOutSpotlightRect | null>(null);
  const marketingOptOutSpotlightRowRef = useRef<HTMLDivElement | null>(null);
  const marketingOptOutSpotlightCleanupRef = useRef<(() => void) | undefined>();

  /** טופס + תצוגה מקדימה נחשפים רק אחרי טעינת טיוטה/רשימת תבניות וכו' (בהרכבה עם ViewRevealer). */
  const [templateSurfaceReady, setTemplateSurfaceReady] = useState(false);

  const [aiPreviewOpen, setAiPreviewOpen] = useState(false);
  const [aiPreviewMode, setAiPreviewMode] = useState<'smart' | 'legacy'>('smart');
  const [previewSmartPayload, setPreviewSmartPayload] = useState<SmartTemplateAiPayload | null>(null);
  const [previewLegacyText, setPreviewLegacyText] = useState('');
  const [smartBriefOpen, setSmartBriefOpen] = useState(false);
  const [smartGenerating, setSmartGenerating] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [manualMetaAiNamingEnabled, setManualMetaAiNamingEnabled] = useState(false);
  const manualMetaAiSubmitStageRef = React.useRef<'idle' | 'need_ai' | 'done'>('idle');

  const bodyTextRef = useRef<HTMLDivElement | null>(null);
  const headerTextRef = useRef<HTMLDivElement | null>(null);
  const niceNameInputRef = useRef<HTMLInputElement>(null);
  const hasAutoFocusedRef = useRef(false);
  const bodyVariableRefsRef = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const warningNotifsRef = useRef<{
    [key: string]: SnackbarKey | null;
  }>({
    name: null,
    header: null,
    body: null,
    footer: null,
    buttons: null,
  });

  const dismissMarketingOptOutSpotlight = useCallback((suppressFuture?: boolean) => {
    if (suppressFuture) writeMarketingOptOutSpotlightSuppress();
    marketingOptOutSpotlightCleanupRef.current?.();
    marketingOptOutSpotlightCleanupRef.current = undefined;
    setMarketingOptOutSpotlightButtonId(null);
    setMarketingOptOutSpotlightRect(null);
  }, []);

  const updateMarketingOptOutSpotlightRect = useCallback(() => {
    const el = marketingOptOutSpotlightRowRef.current;
    if (!el) return;
    const r = getSpotlightRectForOptOutField(el);
    setMarketingOptOutSpotlightRect(padViewportRect(r, 6));
  }, []);

  const syncBodyVariableIndexFromText = useCallback((bodyText: string) => {
    let max = 0;
    const re = /\{\{(\d+)\}\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(bodyText)) !== null) {
      const n = parseInt(m[1], 10);
      if (!Number.isNaN(n) && n > max) max = n;
    }
    nextVariableIndexBody = max + 1;
  }, []);

  const { applySmartPayload, applyLegacyPlainText } = useApplyGeneratedTemplateToForm({
    setSelectedTemplate,
    syncBodyVariableIndexFromText,
  });

  const handleCategory = (category: string) => {
    const upper = category.toUpperCase() as WhatsappTemplateCategory;
    handleTemplateErrors({ category: !category });

    if (upper !== 'MARKETING') {
      setSelectedTemplate((prevObj: WhatsappEditTemplate) => ({
        ...prevObj,
        category: upper,
      }));
      return;
    }

    setSelectedTemplate((prevObj: WhatsappEditTemplate) => {
      const componentsArray = [...prevObj.components];
      if (!componentsArray.some((c) => c.type === 'BUTTONS')) {
        componentsArray.push({
          type: 'BUTTONS',
          format: null,
          text: null,
          buttons: [],
        } as WhatsappOriginalTemplateComponent);
      }

      const targetIdx = componentsArray.findIndex((item) => item.type === 'BUTTONS');
      if (targetIdx === -1) {
        return { ...prevObj, category: upper };
      }

      const targetComponent = { ...componentsArray[targetIdx] } as WhatsappOriginalTemplateButtons;
      const buttons = targetComponent.buttons ? [...targetComponent.buttons] : [];

      const hasOptOut = buttons.some(
        (b) =>
          b.type === 'QUICK_REPLY' &&
          (b.text?.trim() === MARKETING_OPT_OUT_BUTTON_TEXT ||
            b.text?.trim() === AI_MARKETING_OPT_OUT_QUICK_REPLY_TEXT),
      );
      if (hasOptOut) {
        return { ...prevObj, category: upper };
      }

      const hasFlow = buttons.some((b) => b.type === 'FLOW');
      if (hasFlow) {
        queueMicrotask(() =>
          enqueueSnackbar(
            'לא ניתן להוסיף כפתור הסרה אוטומטי כשקיים כפתור זרימה (Flow). יש להסיר את כפתור הזרימה או להוסיף כפתור הסרה ידנית.',
            { variant: 'warning', autoHideDuration: 8000 },
          ),
        );
        return { ...prevObj, category: upper };
      }

      if (buttons.length >= 10) {
        queueMicrotask(() =>
          enqueueSnackbar('הגעת למגבלת הכפתורים — לא ניתן להוסיף כפתור הסרה אוטומטי.', {
            variant: 'warning',
          }),
        );
        return { ...prevObj, category: upper };
      }

      const hasUrlOrPhone = buttons.some((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
      if (hasUrlOrPhone) {
        queueMicrotask(() =>
          enqueueSnackbar(
            'לא ניתן להוסיף כפתור תשובה מהירה "הסר" אוטומטית — כפתורי תשובה מהירה חייבים להופיע לפני כפתורי קישור וטלפון. יש למחוק או לסדר מחדש את הכפתורים, ואז להוסיף כפתור הסרה ידנית.',
            { variant: 'warning', autoHideDuration: 9000 },
          ),
        );
        return { ...prevObj, category: upper };
      }

      const newId = generateUniqueId(buttons.map((b) => b.id || ''));
      const newButton = {
        text: MARKETING_OPT_OUT_BUTTON_TEXT,
        type: 'QUICK_REPLY' as WhatsappTemplateButtonsType,
        id: newId,
      };

      buttons.splice(0, 0, newButton);

      componentsArray[targetIdx] = {
        ...targetComponent,
        buttons,
      };

      queueMicrotask(() => {
        setMarketingOptOutTrackedButtonId(newId);
        if (!readMarketingOptOutSpotlightSuppress()) {
          setMarketingOptOutSpotlightButtonId(newId);
        }
      });

      return {
        ...prevObj,
        category: upper,
        components: componentsArray,
      };
    });
  };

  React.useEffect(() => {
    if (!manualMetaAiNamingEnabled || isEdit) {
      manualMetaAiSubmitStageRef.current = 'idle';
      return;
    }
    manualMetaAiSubmitStageRef.current = 'need_ai';
    setSelectedTemplate((prev) => ({
      ...prev,
      niceName: localization.manualMetaAi.placeholderHebrewName,
      name: MANUAL_METADATA_PLACEHOLDER_NAME_EN,
      /** ברירת מחדל זמנית עד ניתוח AI — לא MARKETING, כדי שלא יתווסף כפתור «הסר» לפני שידוע שהתבנית שיווקית */
      category: prev.category ?? 'UTILITY',
      language: prev.language || 'he',
    }));
  }, [manualMetaAiNamingEnabled, isEdit, localization.manualMetaAi.placeholderHebrewName]);

  const handleLanguage = (language: string) => {
    setSelectedTemplate((prevObj) => {
      return { ...prevObj, language: language };
    });
  };

  const handleLegacyAiAssistantComplete = useCallback((text: string) => {
    setPreviewLegacyText(text);
    setAiPreviewMode('legacy');
    setAiPreviewOpen(true);
  }, []);

  const handleSmartBriefSubmit = useCallback(
    async (brief: string, modelId: string) => {
      setSmartGenerating(true);
      try {
        const existing = buildExistingTemplatesForAiPrompt(whatsappTemplates);
        const result = await generateSmartTemplateAI(brief, { modelId, existingTemplates: existing });
        if (!result.ok) {
          enqueueSnackbar(result.error, { variant: 'error' });
          return;
        }
        setPreviewSmartPayload(result.payload);
        setAiPreviewMode('smart');
        setSmartBriefOpen(false);
        setAiPreviewOpen(true);
      } finally {
        setSmartGenerating(false);
      }
    },
    [whatsappTemplates],
  );

  const handleAiPreviewConfirm = useCallback(() => {
    setManualMetaAiNamingEnabled(false);
    manualMetaAiSubmitStageRef.current = 'idle';
    if (aiPreviewMode === 'smart' && previewSmartPayload) {
      applySmartPayload(previewSmartPayload);
    } else if (aiPreviewMode === 'legacy' && previewLegacyText.trim()) {
      applyLegacyPlainText(previewLegacyText);
    }
    setAiPreviewOpen(false);
    setPreviewSmartPayload(null);
    setPreviewLegacyText('');
  }, [aiPreviewMode, previewSmartPayload, previewLegacyText, applySmartPayload, applyLegacyPlainText]);

  const handleTemplateName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    let value = input.value;

    // Check length limit
    if (value.length > 512) {
      return null;
    }

    // Convert to lowercase and replace spaces with underscores
    value = value.replace(/ /g, '_').toLowerCase();

    // Validate template name according to Meta's rules
    const validateTemplateName = (name: string) => {
      const errors = {
        name: !(name && name !== ''),
        nameInvalidChars: false,
        nameInvalidStartEnd: false,
        nameConsecutiveUnderscores: false,
      };

      if (name) {
        // Check for invalid characters (only lowercase letters, numbers, underscores allowed)
        const invalidCharsRegex = /[^a-z0-9_]/;
        if (invalidCharsRegex.test(name)) {
          errors.nameInvalidChars = true;
        }

        // Check if starts or ends with underscore
        if (name.startsWith('_') || name.endsWith('_')) {
          errors.nameInvalidStartEnd = true;
        }

        // Check for consecutive underscores
        if (name.includes('__')) {
          errors.nameConsecutiveUnderscores = true;
        }
      }

      return errors;
    };

    const validationErrors = validateTemplateName(value);

    setSelectedTemplate((prevObj) => {
      return { ...prevObj, name: value };
    });

    handleTemplateErrors(validationErrors);
  };
  const handleTemplateErrors = (params: Partial<TemplateErrorsType>) => {
    setCreateTemplateErrors((prevStates) => ({ ...prevStates, ...params }));
  };
  const handleTemplateNiceName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const value = input.value;
    setSelectedTemplate((prevObj) => {
      return { ...prevObj, niceName: value };
    });
    handleTemplateErrors({ niceName: !(value && value !== '') });
  };

  const handleTemplateText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    let value = input.value;
    if (value.length > 1024) {
      if (!warningNotifsRef.current.body) {
        return (warningNotifsRef.current.body = enqueueSnackbar(localization.body.maxCharsError, {
          variant: 'warning',
          onClose: () => {
            warningNotifsRef.current.body = null;
          },
        }));
      } else return null;
    }

    const caretPosition = input.selectionStart;

    if (
      value.length > prevValue.length &&
      caretPosition &&
      value.charAt(caretPosition - 2) === '{' &&
      value.charAt(caretPosition - 1) === '{'
    ) {
      // User added symbol {{
      const newValue =
        value.slice(0, caretPosition - 2) + `{{${nextVariableIndexBody}}}` + value.slice(caretPosition);
      value = newValue;

      // Set caret position after insertion
      input.setSelectionRange(
        caretPosition + nextVariableIndexBody.toString().length + 3,
        caretPosition + nextVariableIndexBody.toString().length + 3,
      );
    }

    const varsRegular = /{{(\d+)}}/g;

    if (varsRegular.test(value)) {
      let variables = value.match(varsRegular);

      const wordCount = value.split(/\s+/).filter((word) => word && !word.match(varsRegular)).length;

      let valid = true;
      let errorType = '';

      // Check if body contains only variables (no real text)
      const onlyVariables = value.replace(/\{\{\d+\}\}/g, '').trim() === '';
      if (onlyVariables) {
        valid = false;
        errorType = 'only_variables';
      }

      // Check if body starts with a variable
      else if (/^\{\{\d+\}\}/.test(value.trim())) {
        valid = false;
        errorType = 'starts_with_variable';
      }

      // Check if body ends with a variable
      else if (/\{\{\d+\}\}$/.test(value.trim())) {
        valid = false;
        errorType = 'ends_with_variable';
      }

      // Check if there are more than 10 variables
      else if (variables && variables.length > 10) {
        valid = false;
        errorType = 'too_many_variables';
      }

      // Check for duplicate variables
      else if (variables && variables.length > 1) {
        const variableNumbers = variables.map((v) => v.match(/\d+/)?.[0]);
        const hasDuplicates = variableNumbers.some((num, index) => variableNumbers.indexOf(num) !== index);
        if (hasDuplicates) {
          valid = false;
          errorType = 'duplicate_variable';
        }
      }

      // Check word to variable ratio
      else if (variables && wordCount < 3 + (variables.length - 1) * 2) {
        valid = false;
        errorType = 'insufficient_words';
      }

      if (!valid) {
        setBodyVarsError(true);
        setCreateTemplateErrors((prev) => ({ ...prev, bodyVarsErrorType: errorType }));
        // return;
      } else {
        setBodyVarsError(false);
        setCreateTemplateErrors((prev) => ({ ...prev, bodyVarsErrorType: undefined }));
      }

      nextVariableIndexBody = variables ? variables.length + 1 : 1;
      setSelectedTemplate((prevObj) => {
        const componentsArray = [...prevObj.components];
        let targetComponent = componentsArray.find((item) => item.type === 'BODY');

        if (!targetComponent) return prevObj;

        let newExampleArray: any[] = [];

        variables?.forEach((variable, index) => {
          let variableDefaultValue = '';
          if (targetComponent?.example?.body_text && targetComponent.example?.body_text[0][index]) {
            variableDefaultValue = targetComponent.example?.body_text[0][index];
          }

          newExampleArray.push(variableDefaultValue);
        });

        targetComponent = {
          ...targetComponent,
          example: {
            ...targetComponent.example,
            body_text: [newExampleArray],
          },
        };

        // Update the component in the array
        const componentIndex = componentsArray.findIndex((item) => item.type === 'BODY');
        if (componentIndex !== -1) {
          componentsArray[componentIndex] = targetComponent;
        }

        return { ...prevObj, components: [...componentsArray] };
      });
    } else {
      setBodyVarsError(false);
      setCreateTemplateErrors((prev) => ({ ...prev, bodyVarsErrorType: undefined }));
      nextVariableIndexBody = 1;
      setSelectedTemplate((prevObj) => {
        const componentsArray = [...prevObj.components];
        let targetComponent = componentsArray.find((item) => item.type === 'BODY');
        if (!targetComponent) return prevObj;

        if (targetComponent.example) {
          const { example, ...targetComponentWithoutExample } = targetComponent;
          targetComponent = targetComponentWithoutExample;

          // Update the component in the array
          const componentIndex = componentsArray.findIndex((item) => item.type === 'BODY');
          if (componentIndex !== -1) {
            componentsArray[componentIndex] = targetComponent;
          }
        }

        return { ...prevObj, components: [...componentsArray] };
      });
    }

    prevValue = value;
    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'BODY');
      if (!targetComponent) return prevObj;

      // Create a new component with updated text instead of mutating
      targetComponent = {
        ...targetComponent,
        text: value,
      };

      // Update the component in the array
      const componentIndex = componentsArray.findIndex((item) => item.type === 'BODY');
      if (componentIndex !== -1) {
        componentsArray[componentIndex] = targetComponent;
      }

      return { ...prevObj, components: [...componentsArray] };
    });

    handleTemplateErrors({ bodyText: !(value && value !== '') });
  };

  const addNewTextVariableBody = () => {
    let value = selectedTemplate?.components?.find((item) => item.type === 'BODY')?.text;
    if (!value && value !== '') return;

    // Get cursor position from the input element
    const inputElement = bodyTextRef.current?.querySelector('textarea');
    const cursorPosition = inputElement?.selectionStart ?? value.length;

    // Smart spacing: check characters before and after cursor
    const charBefore = cursorPosition > 0 ? value[cursorPosition - 1] : '';
    const charAfter = cursorPosition < value.length ? value[cursorPosition] : '';

    // Check if we need spaces (if adjacent to letters/numbers)
    const needsSpaceBefore = charBefore && /[a-zA-Z0-9א-ת]/.test(charBefore);
    const needsSpaceAfter = charAfter && /[a-zA-Z0-9א-ת]/.test(charAfter);

    // Build variable text with smart spacing
    const spaceBefore = needsSpaceBefore ? ' ' : '';
    const spaceAfter = needsSpaceAfter ? ' ' : '';
    const variableText = `${spaceBefore}{{${nextVariableIndexBody}}}${spaceAfter}`;

    const lastVariableIndex = value.lastIndexOf('}}');
    if (lastVariableIndex !== -1 && value.length - lastVariableIndex < 3) {
      setBodyVarsError(true);
      setCreateTemplateErrors((prev) => ({ ...prev, bodyVarsErrorType: 'ends_with_variable' }));
    } else {
      // Check if adding this variable would exceed the 10 variable limit
      const varsRegular = /{{(\d+)}}/g;
      const variables = value.match(varsRegular);
      if (variables && variables.length > 10) {
        setBodyVarsError(true);
        setCreateTemplateErrors((prev) => ({ ...prev, bodyVarsErrorType: 'too_many_variables' }));
      } else {
        setBodyVarsError(false);
        setCreateTemplateErrors((prev) => ({ ...prev, bodyVarsErrorType: undefined }));
      }
    }

    // Insert variable at cursor position with smart spacing
    value = value.slice(0, cursorPosition) + variableText + value.slice(cursorPosition);

    const varsRegular = /{{(\d+)}}/g;
    const variables = value.match(varsRegular);

    nextVariableIndexBody = (variables?.length || 0) + 1;

    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'BODY');
      if (!targetComponent) return prevObj;

      // Create a new component with updated text and example instead of mutating
      const existingBodyText = targetComponent.example?.body_text?.[0] || [];
      targetComponent = {
        ...targetComponent,
        text: value,
        example: {
          ...targetComponent.example,
          body_text: [[...existingBodyText, '']],
        },
      };

      // Update the component in the array
      const componentIndex = componentsArray.findIndex((item) => item.type === 'BODY');
      if (componentIndex !== -1) {
        componentsArray[componentIndex] = targetComponent;
      }

      return { ...prevObj, components: [...componentsArray] };
    });

    // Set cursor position after the inserted variable and focus on the example field
    setTimeout(() => {
      // Find the actual position of the new variable in the sorted list
      const newVariableText = variableText.trim(); // Remove spaces for matching
      const sortedVariables: string[] = value.match(varsRegular) || [];
      const actualIndex = sortedVariables.indexOf(newVariableText);

      if (actualIndex !== -1) {
        const inputRef = bodyVariableRefsRef.current[actualIndex];
        if (inputRef) {
          inputRef.focus();
          return;
        }
      }
      // Fallback: return focus to body text if example field not found
      if (inputElement) {
        const newCursorPosition = cursorPosition + variableText.length;
        inputElement.focus();
        inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 100);
  };

  const handleBodyVariableDefaultValue = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const input = e.target;
    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'BODY');
      if (!targetComponent) return prevObj;

      // Create a new body_text array with the updated value instead of mutating
      const currentBodyText = targetComponent.example?.body_text?.[0] || [];
      const newBodyText = [...currentBodyText];
      newBodyText[index] = input.value;

      targetComponent = {
        ...targetComponent,
        example: {
          ...targetComponent.example,
          body_text: [newBodyText],
        },
      };

      // Update the component in the array
      const componentIndex = componentsArray.findIndex((item) => item.type === 'BODY');
      if (componentIndex !== -1) {
        componentsArray[componentIndex] = targetComponent;
      }

      return { ...prevObj, components: [...componentsArray] };
    });
    if (input.value) {
      handleTemplateErrors({
        bodyParams: createTemplateErrors.bodyParams?.filter((paramIndex) => paramIndex !== index) || [],
      });
    } else {
      if (!createTemplateErrors.bodyParams?.some((paramIndex) => paramIndex === index)) {
        handleTemplateErrors({
          bodyParams: [...(createTemplateErrors.bodyParams || []), index],
        });
      }
    }
  };

  const handleTemplateComponents = (
    e: React.ChangeEvent<HTMLInputElement>,
    componentType: WhatsappTemplateComponentType,
  ) => {
    const isActive = e.target.checked;
    if (isActive) {
      const defaultFooterText =
        componentType === 'FOOTER' ? company?.CompanyName?.trim() || '' : '';

      setSelectedTemplate((prevObj) => {
        return {
          ...prevObj,
          components: [
            ...prevObj.components,
            {
              type: componentType,
              text: defaultFooterText,
              format: componentType === 'HEADER' ? 'TEXT' : null,
              buttons: null,
            } as WhatsappOriginalTemplateComponent,
          ],
        };
      });

      if (componentType === 'FOOTER' && defaultFooterText) {
        handleTemplateErrors({ footerText: false });
      }

      const interval = setInterval(() => {
        const componentElement = document.querySelector(
          `.new-template-component-${componentType.toLowerCase()}`,
        );
        const scroller = scrollerRef.current;
        if (componentElement && scroller) {
          const pad = 8;
          const sRect = scroller.getBoundingClientRect();
          const eRect = componentElement.getBoundingClientRect();
          const fullyVisible =
            eRect.top >= sRect.top + pad && eRect.bottom <= sRect.bottom - pad;
          if (!fullyVisible) {
            const nextTop = scroller.scrollTop + (eRect.top - sRect.top) - pad;
            scroller.scrollTo({ top: Math.max(0, nextTop), behavior: 'auto' });
          }
          clearInterval(interval);
        }
      }, 100);
    } else {
      setSelectedTemplate((prevObj) => {
        const componentsArray = prevObj.components.filter((component) => {
          return component.type.toLowerCase() !== componentType.toLowerCase();
        });

        return { ...prevObj, components: [...componentsArray] };
      });

      if (componentType === 'HEADER') {
        handleTemplateErrors({
          headerText: false,
          headerMedia: false,
          headerTextParam: false,
        });
      } else if (componentType === 'FOOTER') {
        handleTemplateErrors({ footerText: false });
      }
    }
  };

  const handleTemplateFooter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const value = input.value;
    if (value.length > 60) {
      if (!warningNotifsRef.current.footer) {
        return (warningNotifsRef.current.footer = enqueueSnackbar(localization.footer.maxCharsError, {
          variant: 'warning',
          onClose: () => {
            warningNotifsRef.current.footer = null;
          },
        }));
      } else return null;
    }
    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'FOOTER');
      if (!targetComponent) return prevObj;

      // Create a new component with updated text instead of mutating
      targetComponent = {
        ...targetComponent,
        text: value,
      };

      // Update the component in the array
      const componentIndex = componentsArray.findIndex((item) => item.type === 'FOOTER');
      if (componentIndex !== -1) {
        componentsArray[componentIndex] = targetComponent;
      }

      return { ...prevObj, components: [...componentsArray] };
    });
    handleTemplateErrors({ footerText: !(value && value !== '') });
  };

  const handleTemplateHeaderType = (headerType: 'text' | 'media') => {
    if (headerType === 'text') {
      setCreateNewTemplateFormStates((prevObj) => {
        return { ...prevObj, headerType: 'text' };
      });
      setSelectedTemplate((prevObj) => {
        const componentsArray = [...prevObj.components];
        let targetComponent = componentsArray.find((item) => item.type === 'HEADER');
        if (!targetComponent) return prevObj;

        // Create a new component with updated format and without example
        const { example, ...targetComponentWithoutExample } = targetComponent;
        targetComponent = {
          ...targetComponentWithoutExample,
          format: 'TEXT',
        };

        // Update the component in the array
        const componentIndex = componentsArray.findIndex((item) => item.type === 'HEADER');
        if (componentIndex !== -1) {
          componentsArray[componentIndex] = targetComponent;
        }

        return { ...prevObj, components: [...componentsArray] };
      });
    } else if (headerType === 'media') {
      setCreateNewTemplateFormStates((prevObj) => {
        return { ...prevObj, headerType: 'media' };
      });
      setSelectedTemplate((prevObj) => {
        const componentsArray = [...prevObj.components];
        let targetComponent = componentsArray.find((item) => item.type === 'HEADER');
        if (!targetComponent) return prevObj;

        // Create a new component with updated format and example
        targetComponent = {
          ...targetComponent,
          format: 'IMAGE',
          example: { header_handle: null },
        };

        // Update the component in the array
        const componentIndex = componentsArray.findIndex((item) => item.type === 'HEADER');
        if (componentIndex !== -1) {
          componentsArray[componentIndex] = targetComponent;
        }

        return { ...prevObj, components: [...componentsArray] };
      });
    }
    setUploadedFileName(null);
    handleTemplateErrors({ headerText: false, headerTextParam: false });
  };
  const handleTemplateHeaderText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    let value = input.value;
    if (value.length > 60) {
      if (!warningNotifsRef.current.header) {
        return (warningNotifsRef.current.header = enqueueSnackbar(
          localization.header.typeText.maxCharsError,
          {
            variant: 'warning',
            onClose: () => {
              warningNotifsRef.current.header = null;
            },
          },
        ));
      } else return null;
    }

    const caretPosition = input.selectionStart;
    const varsRegular = /{{(\d+)}}/g;

    if (
      caretPosition &&
      value.length > prevValue.length &&
      value.charAt(caretPosition - 2) === '{' &&
      value.charAt(caretPosition - 1) === '{' &&
      !varsRegular.test(value)
    ) {
      // User added symbol {{
      value = value.slice(0, caretPosition - 2) + `{{${1}}}` + value.slice(caretPosition);

      // Set caret position after insertion
      input.setSelectionRange(caretPosition + '1'.length + 3, caretPosition + '1'.length + 3);
    }

    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'HEADER');
      if (!targetComponent) return prevObj;

      // Create a new component with updated text and format
      targetComponent = {
        ...targetComponent,
        text: value,
        format: 'TEXT',
      };

      // Update the component in the array
      const componentIndex = componentsArray.findIndex((item) => item.type === 'HEADER');
      if (componentIndex !== -1) {
        componentsArray[componentIndex] = targetComponent;
      }

      return { ...prevObj, components: [...componentsArray] };
    });

    prevValue = value;

    if (varsRegular.test(value)) {
      setSelectedTemplate((prevObj) => {
        const componentsArray = [...prevObj.components];
        let targetComponent = componentsArray.find((item) => item.type === 'HEADER');
        if (!targetComponent) return prevObj;

        targetComponent = {
          ...targetComponent,
          example: { header_text: [''] },
        };

        // Update the component in the array
        const componentIndex = componentsArray.findIndex((item) => item.type === 'HEADER');
        if (componentIndex !== -1) {
          componentsArray[componentIndex] = targetComponent;
        }

        return { ...prevObj, components: [...componentsArray] };
      });
    } else {
      setSelectedTemplate((prevObj) => {
        const componentsArray = [...prevObj.components];
        let targetComponent = componentsArray.find((item) => item.type === 'HEADER');
        if (!targetComponent) return prevObj;
        if (targetComponent.example) {
          delete targetComponent.example;
        }

        return { ...prevObj, components: [...componentsArray] };
      });
    }

    handleTemplateErrors({ headerText: !(value && value !== '') });
  };
  const addNewTextVariableHeader = (e: React.MouseEvent<HTMLButtonElement>) => {
    let value = selectedTemplate.components.find((item) => item.type === 'HEADER')?.text || '';

    // Get cursor position from the input element
    const inputElement = headerTextRef.current?.querySelector('input');
    const cursorPosition = inputElement?.selectionStart ?? value.length;
    const variableText = `{{${1}}}`;

    // Insert variable at cursor position instead of at the end
    value = value.slice(0, cursorPosition) + variableText + value.slice(cursorPosition);

    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'HEADER');
      if (!targetComponent) return prevObj;
      targetComponent = {
        ...targetComponent,
        text: value,
        example: { header_text: [''] },
      };

      // Update the component in the array
      const componentIndex = componentsArray.findIndex((item) => item.type === 'HEADER');
      if (componentIndex !== -1) {
        componentsArray[componentIndex] = targetComponent;
      }

      return { ...prevObj, text: value, components: [...componentsArray] };
    });

    // Set cursor position after the inserted variable
    setTimeout(() => {
      if (inputElement) {
        const newCursorPosition = cursorPosition + variableText.length;
        inputElement.focus();
        inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };
  const handleTemplateHeaderMediaType = (mediaType: WhatsappTemplateHeaderFormat) => {
    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'HEADER');
      if (!targetComponent) return prevObj;
      let updatedComponent;
      if (mediaType !== 'LOCATION') {
        updatedComponent = {
          ...targetComponent,
          format: mediaType,
          example: { header_handle: null },
        };
      } else {
        const { example, ...componentWithoutExample } = targetComponent;
        updatedComponent = {
          ...componentWithoutExample,
          format: mediaType,
        };
      }

      // Update the component in the array
      const componentIndex = componentsArray.findIndex((item) => item.type === 'HEADER');
      if (componentIndex !== -1) {
        componentsArray[componentIndex] = updatedComponent;
      }

      return { ...prevObj, components: [...componentsArray] };
    });
    setUploadedFileName(null);
  };
  const handleHeaderVariableDefaultValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'HEADER');
      if (!targetComponent) return prevObj;

      const currentHeaderText = targetComponent.example?.header_text?.[0] || '';
      targetComponent = {
        ...targetComponent,
        example: {
          ...targetComponent.example,
          header_text: [input.value],
        },
      };

      // Update the component in the array
      const componentIndex = componentsArray.findIndex((item) => item.type === 'HEADER');
      if (componentIndex !== -1) {
        componentsArray[componentIndex] = targetComponent;
      }

      return { ...prevObj, components: [...componentsArray] };
    });
    handleTemplateErrors({
      headerTextParam: !(input.value && input.value !== ''),
    });
  };

  const handleFooterVariableDefaultValue = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const input = e.target;
    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'FOOTER');
      if (!targetComponent) return prevObj;
      const currentFooterText = targetComponent.example?.footer_text?.[0] || [];
      const newFooterText = [...currentFooterText];
      newFooterText[index] = input.value;

      targetComponent = {
        ...targetComponent,
        example: {
          ...targetComponent.example,
          footer_text: [newFooterText],
        },
      };

      // Update the component in the array
      const componentIndex = componentsArray.findIndex((item) => item.type === 'FOOTER');
      if (componentIndex !== -1) {
        componentsArray[componentIndex] = targetComponent;
      }

      return { ...prevObj, components: [...componentsArray] };
    });
    if (input.value) {
      handleTemplateErrors({
        footerParams:
          createTemplateErrors.footerParams?.filter((paramIndex: number) => paramIndex !== index) || [],
      });
    } else {
      if (!createTemplateErrors.footerParams?.some((paramIndex: number) => paramIndex === index)) {
        handleTemplateErrors({
          footerParams: [...(createTemplateErrors.footerParams || []), index],
        });
      }
    }
  };

  async function uploadMediaCreateTemplate(e: React.ChangeEvent<HTMLInputElement>) {
    if (isAuthTemplate) return; // Prevent upload for auth templates
    const file = e.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      const data = await fetcher(
        'post',
        ['/api/wa-business/GetWhatsappMediaHandler', { data: formData }],
        'history',
      );

      if (data?.result === true) {
        const handler = data.handler;
        setSelectedTemplate((prevObj) => {
          const componentsArray = [...prevObj.components];
          let targetComponent = componentsArray.find((item) => item.type === 'HEADER');
          if (!targetComponent) return prevObj;

          targetComponent = {
            ...targetComponent,
            example: {
              ...(targetComponent.example || {}),
              header_handle: handler,
            },
          };

          const componentIndex = componentsArray.findIndex((item) => item.type === 'HEADER');
          if (componentIndex !== -1) {
            componentsArray[componentIndex] = targetComponent;
          }
          return { ...prevObj, components: [...componentsArray] };
        });
        setUploadedFileName(file.name);
        handleTemplateErrors({ headerMedia: false });
      }
    }
    e.target.value = '';
  }

  const createNewTemplateButton = (buttonType: WhatsappTemplateButtonsType) => {
    // Check limits before adding
    const currentButtons =
      selectedTemplate?.components?.find((item) => item.type === 'BUTTONS')?.buttons || [];

    // Check maximum buttons limit
    if (currentButtons.length >= 10) {
      enqueueSnackbar(localization.buttons.maxButtonsError, { variant: 'warning' });
      return;
    }

    // Check URL buttons limit
    if (buttonType === 'URL') {
      const urlButtonsCount = currentButtons.filter((btn: any) => btn.type === 'URL').length;
      if (urlButtonsCount >= 2) {
        enqueueSnackbar(localization.buttons.maxUrlButtonsError, { variant: 'warning' });
        return;
      }
    }

    // Check phone button limit
    if (buttonType === 'PHONE_NUMBER') {
      const hasPhoneButton = currentButtons.some((btn: any) => btn.type === 'PHONE_NUMBER');
      if (hasPhoneButton) {
        enqueueSnackbar(localization.buttons.maxPhoneButtonsError, { variant: 'warning' });
        return;
      }
    }

    // Validate button order before adding
    const hasUrlButtons = currentButtons.some((btn: any) => btn.type === 'URL');
    const hasPhoneButton = currentButtons.some((btn: any) => btn.type === 'PHONE_NUMBER');

    if (buttonType === 'QUICK_REPLY') {
      if (hasUrlButtons || hasPhoneButton) {
        enqueueSnackbar(
          'לא ניתן להוסיף כפתור תשובה מהירה - כפתורי תשובה מהירה חייבים להיות לפני כפתורי קישור וטלפון. יש למחוק את כפתורי הקישור/טלפון או להוסיף את כפתור התשובה המהירה לפניהם.',
          {
            variant: 'error',
            autoHideDuration: 7000,
          },
        );
        return;
      }
    }

    if (buttonType === 'URL') {
      if (hasPhoneButton) {
        enqueueSnackbar(
          'לא ניתן להוסיף כפתור קישור - כפתורי קישור חייבים להיות לפני כפתור הטלפון. יש למחוק את כפתור הטלפון או להוסיף את כפתור הקישור לפניו.',
          {
            variant: 'error',
            autoHideDuration: 7000,
          },
        );
        return;
      }
    }

    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'BUTTONS');
      if (!targetComponent) return prevObj;
      const newButtonsArray = targetComponent.buttons ? [...targetComponent.buttons] : [];
      const newButtonObject =
        buttonType === 'URL'
          ? {
              text: '',
              type: buttonType,
              url: null,
              id: generateUniqueId(newButtonsArray.map((item) => item.id || '')),
            }
          : buttonType === 'PHONE_NUMBER'
            ? {
                text: '',
                type: buttonType,
                phone_number: null,
                id: generateUniqueId(newButtonsArray.map((item) => item.id || '')),
              }
            : buttonType === 'COPY_CODE'
              ? {
                  type: buttonType,
                  id: generateUniqueId(newButtonsArray.map((item) => item.id || '')),
                }
              : buttonType === 'FLOW'
                ? {
                    type: buttonType,
                    text: '',
                    id: generateUniqueId(newButtonsArray.map((item) => item.id || '')),
                    flow_id: '',
                    flow_action: 'navigate',
                    navigate_screen: '',
                  }
                : {
                    text: '',
                    type: buttonType,
                    id: generateUniqueId(newButtonsArray.map((item) => item.id || '')),
                  };
      newButtonsArray.push(newButtonObject);

      // Create a new component with updated buttons instead of mutating
      const updatedComponent = {
        ...targetComponent,
        buttons: [...newButtonsArray],
      };

      // Update the component in the array
      const componentIndex = componentsArray.findIndex((item) => item.type === 'BUTTONS');
      if (componentIndex !== -1) {
        componentsArray[componentIndex] = updatedComponent;
      }

      return { ...prevObj, components: [...componentsArray] };
    });

    setTimeout(() => {
      scrollerRef.current?.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);
  };
  const handleThisButtonText = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const value = e.target.value;
    if (value.length > 25) {
      if (!warningNotifsRef.current.buttons) {
        return (warningNotifsRef.current.buttons = enqueueSnackbar(localization.buttons.maxCharsError, {
          variant: 'warning',
          onClose: () => {
            warningNotifsRef.current.buttons = null;
          },
        }));
      } else return null;
    }
    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'BUTTONS');
      if (!targetComponent) return prevObj;

      // Create a deep copy of the target component to avoid read-only issues
      const targetComponentIndex = componentsArray.findIndex((item) => item.type === 'BUTTONS');
      targetComponent = { ...targetComponent, buttons: [...targetComponent.buttons] };
      componentsArray[targetComponentIndex] = targetComponent;

      // Now we can safely modify the button since we have a mutable copy
      targetComponent.buttons[index] = { ...targetComponent.buttons[index], text: value };

      return { ...prevObj, components: [...componentsArray] };
    });

    // Trigger validation after state update
    setTimeout(() => {
      checkTemplateValidity();
    }, 0);
  };
  const handleThisButtonParamEvent = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
    param: string,
  ) => {
    const input = e.target;
    let value = input.value;
    handleThisButtonParam(value, index, param);
  };
  const handleThisButtonParam = (value: string, index: number, param: string) => {
    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'BUTTONS');
      if (!targetComponent) return prevObj;

      // Create a deep copy of the target component to avoid read-only issues
      const targetComponentIndex = componentsArray.findIndex((item) => item.type === 'BUTTONS');
      targetComponent = { ...targetComponent, buttons: [...targetComponent.buttons] };
      componentsArray[targetComponentIndex] = targetComponent;

      if (param === 'url') {
        const varsRegular = /{{(\d+)}}/g;
        if (value.length > prevValue.length && value.endsWith('{{') && !varsRegular.test(value)) {
          value = value.slice(0, -2) + `{{${1}}}`;
        }

        prevValue = value;
      }

      // Now we can safely modify the button since we have a mutable copy
      if (param === 'text' && varsRegular.test(value)) {
        targetComponent.buttons[index] = { ...targetComponent.buttons[index], example: [''] };
      } else if (param === 'text' && targetComponent.buttons[index].example) {
        const buttonCopy = { ...targetComponent.buttons[index] };
        delete buttonCopy.example;
        targetComponent.buttons[index] = buttonCopy;
      } else {
        targetComponent.buttons[index] = {
          ...targetComponent.buttons[index],
          [param as keyof WhatsappOriginalTemplateButtons['buttons'][0]]: value as any,
        };
      }

      return { ...prevObj, components: [...componentsArray] };
    });

    // Trigger validation after state update
    setTimeout(() => {
      checkTemplateValidity();
    }, 0);
  };
  const handleThisButtonParamExample = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    setSelectedTemplate((prevObj) => {
      const componentsArray = [...prevObj.components];
      let targetComponent = componentsArray.find((item) => item.type === 'BUTTONS');
      if (!targetComponent) return prevObj;

      const input = e.target;
      let value = input.value;

      // Create a deep copy of the target component to avoid read-only issues
      const targetComponentIndex = componentsArray.findIndex((item) => item.type === 'BUTTONS');
      targetComponent = { ...targetComponent, buttons: [...targetComponent.buttons] };
      componentsArray[targetComponentIndex] = targetComponent;

      // Now we can safely modify the button since we have a mutable copy
      if (targetComponent.buttons[index].example) {
        targetComponent.buttons[index] = {
          ...targetComponent.buttons[index],
          example: [value],
        };
      }

      return { ...prevObj, components: [...componentsArray] };
    });
  };
  const addNewTextURLButton = (index: number, currentValue: string) => {
    // URL buttons support only one variable at the end
    const newValue = currentValue + `{{1}}`;
    handleThisButtonParam(newValue, index, 'url');
  };
  const handleFlowBtnType = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    setSelectedTemplate((prevObj) => {
      const input = e.target;
      let value = input.value;

      const componentsArray = prevObj.components.map((comp, compIndex) => {
        if (comp.type === 'BUTTONS') {
          // Create a deep copy of buttons array
          const newButtons = comp.buttons.map((btn, btnIndex) => {
            if (btnIndex === index) {
              // Create new button object with updated values
              const newBtn = { ...btn };
              if (value === 'flow_id' || value === 'flow_json') {
                newBtn[value] = '';
              }
              if (value === 'flow_id' && newBtn['flow_json']) {
                delete newBtn['flow_json'];
              } else if (value === 'flow_json' && newBtn['flow_id']) {
                delete newBtn['flow_id'];
              }
              return newBtn;
            }
            return btn;
          });

          return { ...comp, buttons: newButtons };
        }
        return comp;
      });

      setCreateNewTemplateFormStates((prevObj) => {
        return { ...prevObj, flowType: value as 'flow_json' | 'flow_id' };
      });

      return { ...prevObj, components: componentsArray };
    });
  };
  const deleteThisButton = (index: number) => {
    setSelectedTemplate((prevObj) => {
      const componentsArray = prevObj.components
        .map((comp) => {
          if (comp.type === 'BUTTONS') {
            // Create a deep copy of the buttons array and filter out the deleted button
            const newButtons = comp.buttons.filter((_: any, btnIndex: number) => btnIndex !== index);

            // If no buttons left, return null to remove the BUTTONS component entirely
            if (newButtons.length === 0) {
              return null;
            }

            return {
              ...comp,
              buttons: newButtons,
            };
          }
          return comp;
        })
        .filter((comp): comp is NonNullable<typeof comp> => comp !== null); // Type-safe null removal

      return { ...prevObj, components: componentsArray };
    });
  };

  const checkTemplateValidity = () => {
    // Skip most validations for AUTHENTICATION templates
    const isAuthTemplate = selectedTemplate?.category === 'AUTHENTICATION';

    //! Header
    const headerComponent = selectedTemplate?.components?.find((item) => item.type === 'HEADER');
    const headerVarMatch = headerComponent?.text?.match(varsRegular);
    const headerVar = headerVarMatch ? headerVarMatch[0] : null;

    //! Body
    const bodyComponent = selectedTemplate?.components?.find((item) => item.type === 'BODY');
    const emptyBodyParams =
      bodyComponent?.example?.['body_text']?.[0]
        ?.map((param: any, index: number) => (param === '' ? index : null))
        .filter((index: number | null) => index !== null) || [];
    let bodyVarsError = false;

    if (varsRegular.test(bodyComponent?.text || '')) {
      let variables = bodyComponent?.text?.match(varsRegular);
      const bodyText = bodyComponent?.text || '';

      const wordCount = bodyText?.split(/\s+/).filter((word: any) => word && !word.match(varsRegular)).length;

      let valid = true;
      let errorType = '';

      // Check if body contains only variables (no real text)
      const onlyVariables = bodyText.replace(/\{\{\d+\}\}/g, '').trim() === '';
      if (onlyVariables) {
        valid = false;
        errorType = 'only_variables';
      }

      // Check if body starts with a variable
      else if (/^\{\{\d+\}\}/.test(bodyText.trim())) {
        valid = false;
        errorType = 'starts_with_variable';
      }

      // Check if body ends with a variable
      else if (/\{\{\d+\}\}$/.test(bodyText.trim())) {
        valid = false;
        errorType = 'ends_with_variable';
      }

      // Check if there are more than 10 variables
      else if (variables && variables.length > 10) {
        valid = false;
        errorType = 'too_many_variables';
      }

      // Check for duplicate variables
      else if (variables && variables.length > 1) {
        const variableNumbers = variables.map((v) => v.match(/\d+/)?.[0]);
        const hasDuplicates = variableNumbers.some((num, index) => variableNumbers.indexOf(num) !== index);
        if (hasDuplicates) {
          valid = false;
          errorType = 'duplicate_variable';
        }
      }

      // Check word to variable ratio
      else if (wordCount && wordCount < 3 + ((variables?.length || 0) - 1) * 2) {
        valid = false;
        errorType = 'insufficient_words';
      }

      if (!valid) {
        bodyVarsError = true;
        setBodyVarsError(true);
        // Store error type for different error messages
        setCreateTemplateErrors((prev) => ({ ...prev, bodyVarsErrorType: errorType }));
      } else {
        bodyVarsError = false;
        setBodyVarsError(false);
        setCreateTemplateErrors((prev) => ({ ...prev, bodyVarsErrorType: undefined }));
      }
    } else {
      bodyVarsError = false;
      setBodyVarsError(false);
      setCreateTemplateErrors((prev) => ({ ...prev, bodyVarsErrorType: undefined }));
    }

    //! Footer
    const footerComponent = selectedTemplate?.components?.find((item) => item.type === 'FOOTER');

    //! Buttons
    const buttonsComponent = selectedTemplate?.components?.find((item) => item.type === 'BUTTONS');

    const emptyButtons =
      buttonsComponent?.buttons
        .map((btn: any, originalIndex: number) => {
          let isEmpty = false;
          let hasInvalidUrl = false;

          if (btn.type === 'QUICK_REPLY') {
            isEmpty = !btn.text || btn.text === '';
          } else if (btn.type === 'URL') {
            // Check if URL is valid first (must be a proper URL even with variables)
            if (btn.url && btn.url.trim() !== '') {
              // Replace variables with dummy values for validation
              const urlForValidation = btn.url.replace(/\{\{\d+\}\}/g, 'dummy');
              console.log('Validating URL:', btn.url, '-> transformed:', urlForValidation);
              try {
                new URL(urlForValidation);
                console.log('URL is valid');
              } catch (error) {
                console.log('URL is invalid:', error);
                hasInvalidUrl = true;
              }
            }

            // Check if fields are empty (only if URL is valid or empty)
            if (!hasInvalidUrl) {
              isEmpty = !btn.url || !btn.text || btn.text === '';
            }
          } else if (btn.type === 'PHONE_NUMBER') {
            isEmpty = !btn.phone_number || !btn.text || btn.text === '';
          }

          if (!isEmpty && !hasInvalidUrl) return null;

          let buttonObject: {
            buttonIndex: number;
            fieldTypes: any[];
          } = {
            buttonIndex: originalIndex,
            fieldTypes: [],
          };

          if (btn.type === 'QUICK_REPLY') {
            if (!btn.text || btn.text === '') buttonObject.fieldTypes.push('text');
          } else if (btn.type === 'URL') {
            // Priority: invalid URL > empty URL
            if (hasInvalidUrl) {
              console.log('URL is invalid, adding url_invalid error');
              buttonObject.fieldTypes.push('url_invalid');
            } else if (!btn.url || btn.url.trim() === '') {
              console.log('URL is empty, adding url error');
              buttonObject.fieldTypes.push('url');
            }
            if (!btn.text || btn.text === '') buttonObject.fieldTypes.push('text');
          } else if (btn.type === 'PHONE_NUMBER') {
            if (!btn.phone_number) buttonObject.fieldTypes.push('phone_number');
            if (!btn.text || btn.text === '') buttonObject.fieldTypes.push('text');
          }
          console.log('Button error object:', buttonObject);
          return buttonObject;
        })
        .filter(Boolean) || [];

    // Validate template name according to Meta's rules
    const validateTemplateName = (name: string) => {
      const errors = {
        name: !name || name === '',
        nameInvalidChars: false,
        nameInvalidStartEnd: false,
        nameConsecutiveUnderscores: false,
      };

      if (name) {
        // Check for invalid characters (only lowercase letters, numbers, underscores allowed)
        const invalidCharsRegex = /[^a-z0-9_]/;
        if (invalidCharsRegex.test(name)) {
          errors.nameInvalidChars = true;
        }

        // Check if starts or ends with underscore
        if (name.startsWith('_') || name.endsWith('_')) {
          errors.nameInvalidStartEnd = true;
        }

        // Check for consecutive underscores
        if (name.includes('__')) {
          errors.nameConsecutiveUnderscores = true;
        }
      }

      return errors;
    };

    const nameValidation = validateTemplateName(selectedTemplate.name || '');
    const headerForValidation = headerComponent as WhatsappOriginalTemplateHeader | undefined;
    const newValidationObject = {
      ...nameValidation,
      niceName: !selectedTemplate.niceName || selectedTemplate.niceName === '',
      category: !selectedTemplate.category,
      //! Header - skip validation for auth templates
      headerText: isAuthTemplate
        ? false
        : headerComponent &&
          headerComponent.format === 'TEXT' &&
          (!headerComponent.text || headerComponent.text === ''),
      headerMedia: isAuthTemplate
        ? false
        : Boolean(
            headerForValidation &&
              ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerForValidation.format) &&
              !headerHasMediaHandle(headerForValidation),
          ),
      headerTextParam: isAuthTemplate
        ? false
        : Boolean(
            headerComponent &&
              headerComponent.format === 'TEXT' &&
              headerVar &&
              (!headerComponent.example?.header_text?.[0] || headerComponent.example?.header_text[0] === ''),
          ),
      //! Body - skip validation for auth templates
      bodyText: isAuthTemplate ? false : bodyComponent && (!bodyComponent.text || bodyComponent.text === ''),
      bodyParams: isAuthTemplate ? [] : emptyBodyParams,
      bodyVarsError: isAuthTemplate ? false : bodyVarsError,
      //! Footer - skip validation for auth templates
      footerText: isAuthTemplate
        ? false
        : footerComponent && (!footerComponent.text || footerComponent.text === ''),
      buttons: isAuthTemplate ? [] : emptyButtons,
    };

    handleTemplateErrors(newValidationObject);
    const hasErrors = Object.values(newValidationObject).some(
      (val) =>
        val && (val === true || (val?.toString().length || 0) > 0 || (Array.isArray(val) && val.length > 0)),
    );

    return !hasErrors;
  };

  const saveTemplateToDraft = React.useCallback(
    async (template: WhatsappEditTemplate) => {
      try {
        setSubmittingTemplate(true);
        const { disableSystemAutoUnsubscribe: _stripAutoUnsub, ...templateForDraft } = template;
        const templateCopy = { ...templateForDraft };

        const data = await createUpdateTemplateDraft(
          templateCopy.name,
          templateCopy.niceName,
          JSON.stringify(templateCopy),
          templateType === 'draft' && id ? parseInt(id) : undefined,
        );

        if (data?.result) {
          if (selectedDraftIdRef.current === null) selectedDraftIdRef.current = data.id;
        }
      } catch (error) {
        console.warn('saveTemplateToDraft error: ', error);
        enqueueSnackbar(localization.saveDraftError, {
          variant: 'error',
        });
      } finally {
        setSubmittingTemplate(false);
      }
    },
    [id, localization.saveDraftError, templateType],
  );

  // Handle submit and continue - for rapid template creation
  async function handleSubmitAndContinue() {
    try {
      if (isDuplicateSession && !duplicateMeetsChangeRequirement) {
        enqueueSnackbar(localization.duplicateSession.submitBlocked, { variant: 'warning' });
        return;
      }
      const isValid = checkTemplateValidity();
      console.log('Template validity check:', isValid);
      console.log('Template errors:', createTemplateErrors);
      console.log('Selected template:', selectedTemplate);
      if (!isValid) return;

      // Show immediate feedback
      const submitSnackbar = enqueueSnackbar('התבנית נשלחה לאישור', {
        variant: 'info',
        autoHideDuration: 3000,
      });

      // Create template data for submission - different structure for AUTHENTICATION templates
      let templateCopy;

      if (selectedTemplate.category === 'AUTHENTICATION') {
        // Authentication templates use a special structure
        templateCopy = {
          name: selectedTemplate.name,
          niceName: selectedTemplate.niceName,
          language: selectedTemplate.language || 'he', // Keep as language, not languages
          category: selectedTemplate.category,
          components: [
            {
              type: 'BODY',
              add_security_recommendation: true, // Optional security disclaimer
            },
            {
              type: 'FOOTER',
              code_expiration_minutes: 10, // Optional expiration warning
            },
            {
              type: 'BUTTONS',
              buttons: [
                {
                  type: 'OTP',
                  otp_type: 'COPY_CODE', // Default to copy code button
                },
              ],
            },
          ],
        };
      } else {
        // Regular templates use the existing structure
        templateCopy = {
          name: selectedTemplate.name,
          niceName: selectedTemplate.niceName,
          language: selectedTemplate.language || 'he',
          category: selectedTemplate.category,
          components: selectedTemplate.components.map((comp) => {
            const cleanComp = {
              type: comp.type,
              text: comp.text,
              format: comp.format || null,
              buttons: comp.buttons || null,
              ...((comp as any).example && { example: (comp as any).example }),
            };
            return cleanComp;
          }),
        };
      }

      // Clean up buttons if exists (only for regular templates, not authentication)
      if (selectedTemplate.category !== 'AUTHENTICATION') {
        const buttonsComponent = templateCopy?.components?.find((item) => item.type === 'BUTTONS');
        if (buttonsComponent?.buttons?.length) {
          // Validate button order before submission
          const buttons = buttonsComponent.buttons;
          let lastButtonType: string | null = null;
          let invalidOrder = false;

          for (const btn of buttons) {
            const currentType = btn.type;

            // Check if we're going back to a previous type
            if (lastButtonType === 'URL' && currentType === 'QUICK_REPLY') {
              invalidOrder = true;
              break;
            }
            if (
              lastButtonType === 'PHONE_NUMBER' &&
              (currentType === 'QUICK_REPLY' || currentType === 'URL')
            ) {
              invalidOrder = true;
              break;
            }

            lastButtonType = currentType;
          }

          if (invalidOrder) {
            enqueueSnackbar(
              'לא ניתן להגיש את התבנית - סדר הכפתורים שגוי. יש לארגן את הכפתורים בסדר הבא: תחילה כל כפתורי התשובה המהירה, לאחר מכן כפתורי הקישור, ולבסוף כפתור הטלפון.',
              {
                variant: 'error',
                autoHideDuration: 8000,
              },
            );
            return;
          }

          const componentIndex = templateCopy.components.findIndex((item) => item.type === 'BUTTONS');
          if (componentIndex !== -1) {
            let hasVariables = false;

            const cleanedButtons = buttonsComponent.buttons.map((btn: any) => {
              const { id, ...btnWithoutId } = btn;

              // Format phone number if this is a PHONE button
              if (btnWithoutId.type === 'PHONE_NUMBER' && btnWithoutId.phone_number) {
                btnWithoutId.phone_number = formatPhoneNumberForWhatsApp(btnWithoutId.phone_number);
              }

              // For URL buttons with variables, keep the example
              if (btnWithoutId.type === 'URL' && btnWithoutId.url && /\{\{\d+\}\}/.test(btnWithoutId.url)) {
                hasVariables = true;
                // Keep the example if it exists, otherwise add default
                if (!btnWithoutId.example || !btnWithoutId.example[0]) {
                  btnWithoutId.example = ['example_value'];
                }
              }

              return btnWithoutId;
            });

            templateCopy.components[componentIndex] = {
              ...buttonsComponent,
              buttons: cleanedButtons,
            };
          }
        }
      }

      const params = {
        jsonString: JSON.stringify(templateCopy),
        hebrewName: selectedTemplate.niceName,
        englishName: selectedTemplate.name,
      };

      // Reset form immediately (don't wait for response)
      resetTemplateForm();
      setMarketingOptOutTrackedButtonId(null);
      dismissMarketingOptOutSpotlight(false);

      // Submit in background
      submitTemplateInBackground(params, selectedTemplate.niceName);
    } catch (error) {
      console.error('handleSubmitAndContinue error: ', error);
      enqueueSnackbar('שגיאה בהגשת התבנית', {
        variant: 'error',
      });
    }
  }

  // Background submission with result notifications
  async function submitTemplateInBackground(params: any, templateName: string) {
    try {
      console.log('Sending template creation request with params:', params);
      const data = await fetcher(
        'post',
        [
          isEdit ? '/api/wa-business/editTemplate' : '/api/wa-business/createTemplate',
          {
            data: params,
          },
        ],
        'newapi',
      );

      console.log('Server response:', data);

      if (data) {
        const responseStatus = data.result ? 'success' : 'error';
        const errorMessage = getFacebookError(data);

        enqueueSnackbar(
          responseStatus === 'success' ? `התבנית "${templateName}" הוגשה בהצלחה לאישור` : errorMessage,
          {
            variant: responseStatus,
            autoHideDuration: responseStatus === 'success' ? 5000 : 8000,
          },
        );

        if (responseStatus === 'success') {
          // Refresh templates list in background
          getWhatsappTemplates();
        }
      } else {
        enqueueSnackbar(`שגיאה בהגשת התבנית "${templateName}"`, {
          variant: 'error',
          autoHideDuration: 8000,
        });
      }
    } catch (error) {
      console.error('submitTemplateInBackground error: ', error);
      enqueueSnackbar(`שגיאה בהגשת התבנית "${templateName}"`, {
        variant: 'error',
        autoHideDuration: 8000,
      });
    }
  }

  async function handleSubmitTemplate(close = true) {
    try {
      if (isDuplicateSession && !duplicateMeetsChangeRequirement) {
        enqueueSnackbar(localization.duplicateSession.submitBlocked, { variant: 'warning' });
        return;
      }
      const isValid = checkTemplateValidity();
      console.log('Template validity check:', isValid);
      console.log('Template errors:', createTemplateErrors);
      console.log('Selected template:', selectedTemplate);
      if (!isValid) return;

      if (
        !isEdit &&
        selectedTemplate.category !== 'AUTHENTICATION' &&
        manualMetaAiNamingEnabled &&
        gemini.connected &&
        manualMetaAiSubmitStageRef.current === 'need_ai'
      ) {
        setSubmittingTemplate(true);
        try {
          const existing = buildExistingTemplatesForAiPrompt(whatsappTemplates);
          const draft = serializeTemplateDraftForMetadataAi(selectedTemplate);
          const metaRes = await generateManualTemplateMetadataAI({
            draft,
            existingTemplates: existing,
            modelId: gemini.defaultModel,
          });
          if (!metaRes.ok) {
            enqueueSnackbar(metaRes.error, { variant: 'error' });
            return;
          }
          const { template_name_en, template_name_he, category, language } = metaRes.payload;
          setSelectedTemplate((prev) => ({
            ...prev,
            name: template_name_en,
            niceName: template_name_he,
            category,
            language,
          }));
          window.setTimeout(() => handleCategory(category), 0);
          handleTemplateErrors({
            name: false,
            nameInvalidChars: false,
            nameInvalidStartEnd: false,
            nameConsecutiveUnderscores: false,
            niceName: false,
            category: false,
          });
          manualMetaAiSubmitStageRef.current = 'done';
          enqueueSnackbar(localization.manualMetaAi.reviewThenSubmitAgain, {
            variant: 'info',
            autoHideDuration: 8000,
          });
        } finally {
          setSubmittingTemplate(false);
        }
        return;
      }

      if (!isEdit && selectedTemplate.name === MANUAL_METADATA_PLACEHOLDER_NAME_EN) {
        enqueueSnackbar(localization.manualMetaAi.submitBlockedPlaceholder, { variant: 'warning' });
        return;
      }

      const infoSnackbar = enqueueSnackbar(currentLocale.settingsPopup.templates.notifications.submitInfo, {
        variant: 'info',
      });
      // Create template object - different structure for AUTHENTICATION templates
      let templateCopy;

      if (selectedTemplate.category === 'AUTHENTICATION') {
        // Authentication templates use a special structure
        templateCopy = {
          name: selectedTemplate.name,
          niceName: selectedTemplate.niceName,
          language: selectedTemplate.language || 'he', // Keep as language, not languages
          category: selectedTemplate.category,
          components: [
            {
              type: 'BODY',
              add_security_recommendation: true, // Optional security disclaimer
            },
            {
              type: 'FOOTER',
              code_expiration_minutes: 10, // Optional expiration warning
            },
            {
              type: 'BUTTONS',
              buttons: [
                {
                  type: 'OTP',
                  otp_type: 'COPY_CODE', // Default to copy code button
                },
              ],
            },
          ],
        };
      } else {
        // Regular templates use the existing structure
        templateCopy = {
          name: selectedTemplate.name,
          niceName: selectedTemplate.niceName,
          language: selectedTemplate.language || 'he',
          category: selectedTemplate.category,
          components: selectedTemplate.components.map((comp) => {
            // Clean up component - remove unnecessary fields
            const cleanComp = {
              type: comp.type,
              text: comp.text,
              format: comp.format || null,
              buttons: comp.buttons || null,
              ...((comp as any).example && { example: (comp as any).example }),
            };
            return cleanComp;
          }),
        };
      }
      const templateOriginalCopy = { ...selectedTemplate };

      // Clean up buttons if exists (only for regular templates, not authentication)
      if (selectedTemplate.category !== 'AUTHENTICATION') {
        const buttonsComponent = templateCopy?.components?.find((item) => item.type === 'BUTTONS');
        if (buttonsComponent?.buttons?.length) {
          const componentIndex = templateCopy.components.findIndex((item) => item.type === 'BUTTONS');
          if (componentIndex !== -1) {
            let hasVariables = false;

            const cleanedButtons = buttonsComponent.buttons.map((btn: any) => {
              const { id, ...btnWithoutId } = btn;

              // Format phone number if this is a PHONE button
              if (btnWithoutId.type === 'PHONE_NUMBER' && btnWithoutId.phone_number) {
                btnWithoutId.phone_number = formatPhoneNumberForWhatsApp(btnWithoutId.phone_number);
              }

              // For URL buttons with variables, keep the example
              if (btnWithoutId.type === 'URL' && btnWithoutId.url && /\{\{\d+\}\}/.test(btnWithoutId.url)) {
                hasVariables = true;
                // Keep the example if it exists, otherwise add default
                if (!btnWithoutId.example || !btnWithoutId.example[0]) {
                  btnWithoutId.example = ['example_value'];
                }
              }

              return btnWithoutId;
            });

            templateCopy.components[componentIndex] = {
              ...buttonsComponent,
              buttons: cleanedButtons,
            };
          }
        }
      }

      // Create params object - only include id for edit operations
      console.log('Building params - isEdit:', isEdit, 'selectedTemplate.id:', selectedTemplate.id);
      const params = isEdit
        ? {
            jsonString: JSON.stringify(templateCopy),
            hebrewName: selectedTemplate.niceName,
            englishName: selectedTemplate.name,
            id: selectedTemplate.id,
          }
        : {
            jsonString: JSON.stringify(templateCopy),
            hebrewName: selectedTemplate.niceName,
            englishName: selectedTemplate.name,
          };

      creationAction();

      if (!close) setSubmittingTemplate(true);
      if (close) navigate('/templates/list');

      async function creationAction() {
        console.log('isEdit:', isEdit);
        console.log('id:', id);
        console.log('selectedTemplate.id:', selectedTemplate.id);
        console.log('Sending template creation request with params:', params);
        const data = await fetcher(
          'post',
          [
            isEdit ? '/api/wa-business/editTemplate' : '/api/wa-business/createTemplate',
            {
              data: params,
            },
          ],
          'newapi',
        );

        console.log('Server response:', data);

        if (data) {
          const responseStatus = data.result ? 'success' : 'error';
          closeSnackbar(infoSnackbar);
          const errorMessage = getFacebookError(data);
          enqueueSnackbar(
            responseStatus === 'success'
              ? `${currentLocale.settingsPopup.templates.notifications.success.before} ${selectedTemplate.niceName} ${currentLocale.settingsPopup.templates.notifications.success.after}`
              : // : data.errorObj?.error?.message || data.message,
                errorMessage,
            { variant: responseStatus, autoHideDuration: 10000 },
          );
          if (responseStatus === 'success') {
            console.log('✅ Template saved successfully, refreshing templates...');
            await getWhatsappTemplates();
            console.log('🔄 Templates refresh completed');
            //? if (!close) openCreateNewTemplateForm();
          } else {
            if (close) {
              handleErrorPopup({ show: true, template: templateOriginalCopy });
            }
          }
        } else {
          closeSnackbar(infoSnackbar);
          enqueueSnackbar(currentLocale.settingsPopup.templates.notifications.createTemplate.error, {
            variant: 'error',
          });
          if (close) {
            handleErrorPopup({ show: true, template: templateOriginalCopy });
          }
        }
        if (!close) setSubmittingTemplate(false);
      }
    } catch (error) {
      if (!close) setSubmittingTemplate(false);
      console.error('handleSubmitTemplate error: ', error);
    }
  }

  const { bodyVariables, headerVariable, footerVariables } = React.useMemo(() => {
    let bodyVarsList = selectedTemplate?.components
      ?.find((item) => item.type === 'BODY')
      ?.text?.match(varsRegular)
      ? selectedTemplate.components.find((item) => item.type === 'BODY')?.text?.match(varsRegular)
      : [];
    const headerVar = selectedTemplate?.components
      ?.find((item) => item.type === 'HEADER')
      ?.text?.match(varsRegular)
      ? selectedTemplate.components.find((item) => item.type === 'HEADER')?.text?.match(varsRegular)?.[0]
      : null;
    // Footer variables are not allowed according to Meta policy
    let footerVarsList: string[] = [];

    return {
      bodyVariables: bodyVarsList,
      headerVariable: headerVar,
      footerVariables: footerVarsList,
    };
  }, [selectedTemplate.components]);

  const buttonsList = selectedTemplate?.components?.find((item) => item.type === 'BUTTONS')?.buttons;

  useLayoutEffect(() => {
    if (!marketingOptOutSpotlightButtonId) {
      setMarketingOptOutSpotlightRect(null);
      return;
    }

    let cancelled = false;
    let retryTimer: number | undefined;

    const attachListeners = (el: HTMLElement) => {
      const t1 = window.setTimeout(() => {
        if (!cancelled) updateMarketingOptOutSpotlightRect();
      }, 520);
      const t2 = window.setTimeout(() => {
        if (!cancelled) updateMarketingOptOutSpotlightRect();
      }, 900);
      const onScrollOrResize = () => updateMarketingOptOutSpotlightRect();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => updateMarketingOptOutSpotlightRect()) : null;
      const obsEl =
        (el.querySelector('[data-marketing-opt-out-field="true"]') as HTMLElement | null) || el;
      if (ro) ro.observe(obsEl);
      marketingOptOutSpotlightCleanupRef.current = () => {
        clearTimeout(t1);
        clearTimeout(t2);
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
        ro?.disconnect();
      };
    };

    const trySetup = (attempt: number) => {
      const el = marketingOptOutSpotlightRowRef.current;
      if (cancelled) return;
      if (!el) {
        if (attempt < 24) {
          retryTimer = window.setTimeout(() => trySetup(attempt + 1), 60);
        }
        return;
      }
      updateMarketingOptOutSpotlightRect();
      const anchor = el.querySelector('[data-marketing-opt-out-field="true"]') as HTMLElement | null;
      const inputRoot = anchor?.querySelector('.MuiOutlinedInput-root') as HTMLElement | null;
      (inputRoot || anchor || el).scrollIntoView({ behavior: 'smooth', block: 'center' });
      attachListeners(el);
    };

    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => trySetup(0));
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      marketingOptOutSpotlightCleanupRef.current?.();
      marketingOptOutSpotlightCleanupRef.current = undefined;
    };
  }, [marketingOptOutSpotlightButtonId, updateMarketingOptOutSpotlightRect]);

  /** overflow:hidden מסיר את פס הגלילה וגורם לרוחב התוכן להשתנות (בולט ב־RTL) — מפצים ב-padding-inline-end */
  React.useEffect(() => {
    if (!marketingOptOutSpotlightButtonId || !marketingOptOutSpotlightRect) return;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingInlineEnd = document.body.style.paddingInlineEnd;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingInlineEnd = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingInlineEnd = prevPaddingInlineEnd;
    };
  }, [marketingOptOutSpotlightButtonId, marketingOptOutSpotlightRect]);

  React.useEffect(() => {
    if (!marketingOptOutSpotlightButtonId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismissMarketingOptOutSpotlight(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [marketingOptOutSpotlightButtonId, dismissMarketingOptOutSpotlight]);

  React.useEffect(() => {
    if (!marketingOptOutTrackedButtonId || !buttonsList?.length) return;
    const exists = buttonsList.some((b: { id?: string }) => b.id === marketingOptOutTrackedButtonId);
    if (!exists) {
      const tid = marketingOptOutTrackedButtonId;
      if (marketingOptOutSpotlightButtonId === tid) dismissMarketingOptOutSpotlight(false);
      setMarketingOptOutTrackedButtonId(null);
    }
  }, [
    buttonsList,
    marketingOptOutTrackedButtonId,
    marketingOptOutSpotlightButtonId,
    dismissMarketingOptOutSpotlight,
  ]);

  const isMaxAllButtons =
    (selectedTemplate?.components?.find((item) => item.type === 'BUTTONS')?.buttons?.length || 0) >= 10;
  const isMaxURLButtons =
    (selectedTemplate?.components
      ?.find((item) => item.type === 'BUTTONS')
      ?.buttons?.filter((btn: any) => btn.type === 'URL')?.length || 0) >= 2;
  const isPhoneButton = selectedTemplate?.components
    ?.find((item) => item.type === 'BUTTONS')
    ?.buttons?.some((btn: any) => btn.type === 'PHONE_NUMBER');
  const isFlowButton = selectedTemplate?.components
    ?.find((item) => item.type === 'BUTTONS')
    ?.buttons?.some((btn: any) => btn.type === 'FLOW');

  // Check desktop compatibility
  const hasDesktopCompatibilityIssue = React.useMemo(() => {
    const buttonsComponent = selectedTemplate?.components?.find((item) => item.type === 'BUTTONS');
    if (!buttonsComponent?.buttons?.length) return false;

    const buttons = buttonsComponent.buttons;
    const buttonCount = buttons.length;

    // 4+ buttons won't work on desktop
    if (buttonCount >= 4) return true;

    // Quick reply button + other button types won't work on desktop
    const hasQuickReply = buttons.some((btn: any) => btn.type === 'QUICK_REPLY');
    const hasOtherTypes = buttons.some((btn: any) => btn.type !== 'QUICK_REPLY');

    return hasQuickReply && hasOtherTypes;
  }, [selectedTemplate?.components]);

  // Check if template is AUTHENTICATION category - disable editing controls
  const isAuthTemplate = selectedTemplate?.category === 'AUTHENTICATION';

  React.useEffect(() => {
    if (isAuthTemplate) {
      setManualMetaAiNamingEnabled(false);
      manualMetaAiSubmitStageRef.current = 'idle';
    }
  }, [isAuthTemplate]);

  const getSingleDraftTemplate = React.useCallback(async (id: string) => {
    const data = await fetchGetDraftTemplateById(id);
    if (data?.result) {
      const parsedTemplate = JSON.parse(data.template.DraftTemplatObject) as WhatsappEditTemplate;
      setSelectedTemplate(normalizeTemplateForEditor(parsedTemplate));
      selectedDraftIdRef.current = parseInt(id);
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    selectedDraftIdRef.current = null;
    setTemplateSurfaceReady(false);

    (async () => {
      try {
        if (id) {
          if (templateType === 'draft') {
            await getSingleDraftTemplate(id);
          } else {
            await getWhatsappTemplates();
            if (cancelled) return;
            const tplState = store.getState().templates;
            const existingOriginalTemplate = tplState.whatsappOriginalTemplates.find((templ) => templ.id === id);
            const existingTemplate = tplState.whatsappTemplates.find((templ) => templ.id === id);
            if (existingOriginalTemplate && existingTemplate) {
              if (!existingOriginalTemplate.category) existingOriginalTemplate.category = '';
              const templateObject: WhatsappEditTemplate = normalizeTemplateForEditor({
                ...existingOriginalTemplate,
                niceName:
                  (existingTemplate.hebrewName as any)?.hebrewTemaplateName ||
                  existingTemplate.hebrewName?.hebrewTemaplateName ||
                  '',
              });
              setSelectedTemplate(templateObject);
            }
          }
        } else {
          const routeState = location.state as {
            catalogMetaPrefill?: WhatsappEditTemplate;
            duplicateTemplatePrefill?: WhatsappEditTemplate;
            duplicateSourceLabel?: string;
          };
          const duplicatePrefill = routeState?.duplicateTemplatePrefill;
          const catalogPrefill = routeState?.catalogMetaPrefill;
          if (duplicatePrefill && !appliedCatalogFromStateRef.current) {
            appliedCatalogFromStateRef.current = true;
            const normalized = normalizeTemplateForEditor(duplicatePrefill);
            setSelectedTemplate(normalized);
            duplicateBaselineRef.current = buildDuplicateSessionBaseline(normalized);
            setIsDuplicateSession(true);
            setDuplicateSourceLabel(routeState?.duplicateSourceLabel?.trim() || normalized.niceName || normalized.name || '');
            navigate(location.pathname, { replace: true, state: {} });
            return;
          }
          if (catalogPrefill && !appliedCatalogFromStateRef.current) {
            appliedCatalogFromStateRef.current = true;
            setSelectedTemplate(normalizeTemplateForEditor(catalogPrefill));
            navigate(location.pathname, { replace: true, state: {} });
            return;
          }
          if (appliedCatalogFromStateRef.current) {
            return;
          }
          const storeState = store.getState();
          const errorTemplate = storeState?.templates?.errorPopup?.template;
          if (errorTemplate) {
            setSelectedTemplate(normalizeTemplateForEditor(JSON.parse(JSON.stringify(errorTemplate))));
            handleErrorPopup({
              template: null,
            });
          }
        }
      } catch (e) {
        console.warn('Template surface bootstrap:', e);
      } finally {
        if (!cancelled) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setTemplateSurfaceReady(true));
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, templateType, getSingleDraftTemplate, location.pathname, location.state, navigate]);

  /** סנכרון רדיו «טקסט / מדיה» עם format הכותרת בטעינת עריכה משרת */
  React.useEffect(() => {
    const header = selectedTemplate?.components?.find((c) => c.type === 'HEADER') as
      | WhatsappOriginalTemplateHeader
      | undefined;
    if (!header) return;
    const fmt = String(header.format || '');
    const isMedia = ['IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'].includes(fmt);
    setCreateNewTemplateFormStates((prev) => {
      const next: 'text' | 'media' = isMedia ? 'media' : 'text';
      if (prev.headerType === next) return prev;
      return { ...prev, headerType: next };
    });
  }, [selectedTemplate]);

  /** פתיחת תצוגה מקדימה רק אחרי שהטופס נטען (ביחד עם ViewRevealer). */
  React.useEffect(() => {
    if (!templateSurfaceReady) return;

    const isMobile = window.innerWidth < 768;
    const openPreview = () => {
      openRightSidebar('templates-create-preview-sidebar', {
        hideHeader: true,
        width: isMobile ? Math.min(Math.floor(window.innerWidth * 0.9 * 1.25), window.innerWidth - 16) : 460,
      });
    };

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        openPreview();
      });
    });

    return () => {
      cancelAnimationFrame(raf);
      const activeId = store.getState().dualSidebar.right.activeId;
      if (activeId === 'templates-create-preview-sidebar') {
        closeRightSidebar();
      }
    };
  }, [templateSurfaceReady]);

  // Focus on niceName field and position cursor at end when component mounts
  React.useEffect(() => {
    if (!hasAutoFocusedRef.current && !isEdit) {
      const timer = setTimeout(() => {
        if (niceNameInputRef.current) {
          const inputElement = niceNameInputRef.current.querySelector('input');
          if (inputElement) {
            inputElement.focus();
            // Position cursor at end of text
            const length = inputElement.value.length;
            inputElement.setSelectionRange(length, length);
            hasAutoFocusedRef.current = true;
          }
        }
      }, 680);

      return () => clearTimeout(timer);
    }
  }, [isEdit]);

  return (
    <ViewRevealer
      loading={!templateSurfaceReady}
      mode="block"
      minHeight={420}
      sx={{
        flex: '1 1 0%',
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
        boxSizing: 'border-box',
      }}
      contentSx={{
        flex: '1 1 0%',
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {selectedTemplate ? (
        <Box
          sx={{
            flex: '1 1 0%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Breadcrumbs
            custom
            heading={isEdit ? 'עריכת תבנית' : 'יצירת תבנית חדשה'}
            links={[
              { title: 'Home', to: DASHBOARD_PATH, icon: BreadCrumbsHomeIcon, onlyIcon: true },
              { title: 'תבניות', to: '/templates/list' },
              { title: isEdit ? 'עריכת תבנית' : 'יצירת תבנית' },
            ]}
            sx={{ flexShrink: 0, mb: '24px' }}
          />

          <Box
            data-tour="template-editor-card"
            sx={{
              width: '100%',
              flex: '1 1 0%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <MainCard
        fullHeightPage
        contentSX={{
          pb: { xs: 1.5, md: 2 },
          /** בלי scroll anchoring — החלפת כותרת מדיה/טקסט לא מזיזה מעט את הגלילה */
          overflowAnchor: 'none',
        }}
        sx={{
          flex: '1 1 0%',
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          '& .MuiCardHeader-action': {
            margin: 0,
            padding: 0,
            width: '100%',
          },
        }}
        secondary={
          isMobile ? (
            // Mobile layout
            <Box sx={{ width: '100%', position: 'relative' }}>
              {/* Close button in top right corner */}
              <IconButton
                onClick={() => navigate('/templates/list')}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  zIndex: 10,
                }}
              >
                <CloseTwoTone />
              </IconButton>

              {/* Title */}
              <Box sx={{ mb: 2, pr: 6 }}>
                <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 400 }}>
                  {selectedTemplate.niceName || selectedTemplate.name || 'תבנית חדשה'}
                </Typography>
              </Box>

              {/* Action buttons in a row */}
              <Grid container spacing={1} justifyContent="center" alignItems="center">
                <Grid>
                  <Tooltip
                    title={!gemini.connected ? geminiNoConnectionTooltip : ''}
                    disableHoverListener={gemini.connected}
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AutoAwesome />}
                        disabled={isAuthTemplate || submittingTemplate || !gemini.connected}
                        onClick={() => {
                          if (isAuthTemplate || !gemini.connected) return;
                          setSmartBriefOpen(true);
                        }}
                        sx={{
                          pointerEvents: submittingTemplate ? 'none' : 'initial',
                          opacity: submittingTemplate ? 0.75 : 1,
                          fontSize: '0.75rem',
                          px: 1,
                          fontWeight: 600,
                        }}
                      >
                        ניסוח חכם (AI ✨)
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
                <Grid>
                  <IconButton
                    aria-label="אפשרויות נוספות"
                    onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                    disabled={isAuthTemplate || submittingTemplate}
                    size="small"
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu anchorEl={moreMenuAnchor} open={Boolean(moreMenuAnchor)} onClose={() => setMoreMenuAnchor(null)}>
                    <MenuItem
                      onClick={() => {
                        setMoreMenuAnchor(null);
                        if (isAuthTemplate) return;
                        openRightSidebar('template-ai-helper', {
                          width: 440,
                          title: 'מחולל ישן (Legacy)',
                        });
                      }}
                      disabled={isAuthTemplate || submittingTemplate}
                    >
                      מחולל ישן (Legacy)
                    </MenuItem>
                  </Menu>
                </Grid>
                {/* <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSubmitAndContinue()}
                    disabled={submittingTemplate}
                    sx={{
                      pointerEvents: submittingTemplate ? 'none' : 'initial',
                      opacity: submittingTemplate ? 0.75 : 1,
                      fontSize: '0.75rem',
                      px: 1,
                    }}
                  >
                    {localization.actionButtons.createNoClose}
                  </Button>
                </Grid> */}
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSubmitTemplate(true)}
                    disabled={submittingTemplate || (isDuplicateSession && !duplicateMeetsChangeRequirement)}
                    sx={{
                      pointerEvents: submittingTemplate ? 'none' : 'initial',
                      opacity: submittingTemplate ? 0.75 : 1,
                      fontSize: '0.75rem',
                      px: 1,
                    }}
                  >
                    הגש
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ) : (
            // Desktop layout (unchanged)
            <Grid
              container
              alignItems={'center'}
              justifyContent={'space-between'}
              sx={{
                width: '100%',
                margin: 0,
                padding: 0,
              }}
            >
              <Grid>
                <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 400 }}>
                  {selectedTemplate.niceName || selectedTemplate.name || 'תבנית חדשה'}
                </Typography>
              </Grid>
              <Grid container spacing={2.5} alignItems={'center'} sx={{ width: 'auto' }}>
                <Grid>
                  <Tooltip
                    title={!gemini.connected ? geminiNoConnectionTooltip : ''}
                    disableHoverListener={gemini.connected}
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AutoAwesome />}
                        disabled={isAuthTemplate || submittingTemplate || !gemini.connected}
                        onClick={() => {
                          if (isAuthTemplate || !gemini.connected) return;
                          setSmartBriefOpen(true);
                        }}
                        sx={{
                          pointerEvents: submittingTemplate ? 'none' : 'initial',
                          opacity: submittingTemplate ? 0.75 : 1,
                          fontWeight: 600,
                        }}
                      >
                        ניסוח חכם (AI ✨)
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
                <Grid>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                    disabled={isAuthTemplate || submittingTemplate}
                    sx={{ minWidth: 0, px: 1.25 }}
                  >
                    אפשרויות נוספות
                  </Button>
                  <Menu anchorEl={moreMenuAnchor} open={Boolean(moreMenuAnchor)} onClose={() => setMoreMenuAnchor(null)}>
                    <MenuItem
                      onClick={() => {
                        setMoreMenuAnchor(null);
                        if (isAuthTemplate) return;
                        openRightSidebar('template-ai-helper', {
                          width: 440,
                          title: 'מחולל ישן (Legacy)',
                        });
                      }}
                      disabled={isAuthTemplate || submittingTemplate}
                    >
                      מחולל ישן (Legacy)
                    </MenuItem>
                  </Menu>
                </Grid>
                {/* <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSubmitAndContinue()}
                    disabled={submittingTemplate}
                    sx={{
                      pointerEvents: submittingTemplate ? 'none' : 'initial',
                      opacity: submittingTemplate ? 0.75 : 1,
                      '& .MuiButton-endIcon': {
                        mr: -3.5,
                      },
                    }}
                  >
                    {localization.actionButtons.createNoClose}
                  </Button>
                </Grid> */}
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSubmitTemplate(true)}
                    disabled={submittingTemplate || (isDuplicateSession && !duplicateMeetsChangeRequirement)}
                    sx={{
                      pointerEvents: submittingTemplate ? 'none' : 'initial',
                      opacity: submittingTemplate ? 0.75 : 1,
                      '& .MuiButton-endIcon': {
                        mr: -3.5,
                      },
                    }}
                  >
                    הגש
                  </Button>
                </Grid>
                <Grid>
                  <IconButton onClick={() => navigate('/templates/list')}>
                    <CloseTwoTone />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          )
        }
        scrollPage
        scrollerRef={scrollerRef}
      >
        {submittingTemplate && <Loader />}

        {isDuplicateSession && !duplicateMeetsChangeRequirement ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {localization.duplicateSession.banner.replace(
              '{source}',
              duplicateSourceLabel || selectedTemplate.niceName || selectedTemplate.name || '',
            )}
          </Alert>
        ) : null}

        <Grid container spacing={gridSpacing}>
          <Grid size={{ md: 6, xs: 12 }}>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'background.paper',
                pb: 2,
                borderBottom: '1px solid #0000001F',
                mb: 2,
              }}
            >
              <Grid container spacing={1}>
                {!isEdit && !isAuthTemplate ? (
                  <Grid size={12}>
                    <Tooltip
                      title={!gemini.connected ? geminiNoConnectionTooltip : ''}
                      disableHoverListener={gemini.connected}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        useFlexGap
                        sx={{ display: 'inline-flex', flexWrap: 'wrap', rowGap: 0.5, width: '100%' }}
                      >
                        <Switch
                          checked={manualMetaAiNamingEnabled}
                          onChange={(_, checked) => setManualMetaAiNamingEnabled(checked)}
                          color="primary"
                          disabled={!gemini.connected}
                          inputProps={{ 'aria-label': localization.manualMetaAi.toggleLabel }}
                        />
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ display: 'inline-flex', flexWrap: 'wrap', rowGap: 0.5, flex: 1, minWidth: 0 }}
                        >
                          <Typography component="span" variant="body2" sx={{ textAlign: 'start' }}>
                            {localization.manualMetaAi.toggleLabel}
                          </Typography>
                          <ContextualInfoPopover
                            title={localization.manualMetaAi.infoTitle}
                            body={localization.manualMetaAi.infoBody}
                            hoverHint={localization.manualMetaAi.infoHoverHint}
                            size="dense"
                          />
                        </Stack>
                      </Stack>
                    </Tooltip>
                  </Grid>
                ) : null}
                {/* Template name (nice name first) */}
                <Grid size={12}>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <TextField
                      ref={niceNameInputRef}
                      onChange={handleTemplateNiceName}
                      value={selectedTemplate.niceName || ''}
                      fullWidth
                      label={localization.niceName.title}
                      helperText={
                        createTemplateErrors.niceName
                          ? errorTexts.textInput
                          : localization.niceName.bottomText
                      }
                      error={createTemplateErrors.niceName}
                      autoFocus={true}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid size={12} sx={{ mt: 2 }}>
                  <Grid size={{ md: 6, xs: 12 }}>
                    <TextField
                      onChange={handleTemplateName}
                      value={selectedTemplate.name || ''}
                      fullWidth
                      label={localization.name.title}
                      helperText={
                        createTemplateErrors.name
                          ? errorTexts.textInput
                          : createTemplateErrors.nameInvalidChars
                            ? localization.name.invalidCharsError
                            : createTemplateErrors.nameInvalidStartEnd
                              ? localization.name.invalidStartEndError
                              : createTemplateErrors.nameConsecutiveUnderscores
                                ? localization.name.consecutiveUnderscoresError
                                : localization.name.bottomText
                      }
                      error={
                        createTemplateErrors.name ||
                        createTemplateErrors.nameInvalidChars ||
                        createTemplateErrors.nameInvalidStartEnd ||
                        createTemplateErrors.nameConsecutiveUnderscores
                      }
                      disabled={isEdit}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ mt: 2, mb: 2 }} />
                {/* Template category */}
                <Grid size={12}>
                  <FormControl
                    fullWidth
                    sx={{ mt: 1 }}
                    disabled={isEdit && selectedTemplate.status === 'APPROVED'}
                    error={createTemplateErrors.category}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {localization.category.title}
                    </Typography>
                    <RadioGroup
                      row
                      value={selectedTemplate.category || null}
                      onChange={(e) => handleCategory(e.target.value)}
                    >
                      <FormControlLabel
                        value="MARKETING"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Campaign sx={{ color: 'primary.main', fontSize: 16 }} />
                            {getCategoryText('MARKETING')}
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="UTILITY"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Notifications sx={{ color: 'info.main', fontSize: 16 }} />
                            {getCategoryText('UTILITY')}
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="AUTHENTICATION"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Lock sx={{ color: 'primary.main', fontSize: 16 }} />
                            {getCategoryText('AUTHENTICATION')}
                          </Box>
                        }
                      />
                    </RadioGroup>
                    {createTemplateErrors.category && <FormHelperText>יש לבחור קטגוריה</FormHelperText>}
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        display: 'block',
                        color: 'text.secondary',
                        fontStyle: 'italic',
                      }}
                    >
                      לתשומת לבך: לאחר הגשת התבנית מתבצעת בדיקה ע"י META וסיווג התבנית עשוי להשתנות באופן
                      אוטומטי.
                    </Typography>
                  </FormControl>
                </Grid>
                <Divider sx={{ mt: 1, mb: 1 }} />
                {/* Template language */}
                <Grid size={12}>
                  <FormControl fullWidth sx={{ mt: 1 }} disabled={isEdit}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {localization.language.title}
                    </Typography>
                    <RadioGroup
                      row
                      value={
                        allowedLanguages.find((lang) => lang.code === selectedTemplate.language)?.code || ''
                      }
                      onChange={(e) => handleLanguage(e.target.value)}
                    >
                      {allowedLanguages.map((language) => (
                        <FormControlLabel
                          key={language.code}
                          value={language.code}
                          control={<Radio />}
                          label={getLanguageText(language.code, language.language)}
                        />
                      ))}
                    </RadioGroup>
                    <FormHelperText>{localization.language.bottomText}</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <Box sx={{ pl: 2, borderLeft: '1px solid #0000001F' }}>
              {/* Components controls */}
              <Grid container spacing={1.25} alignItems={'center'}>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <RadioGroup
                      row
                      sx={{ flexWrap: 'wrap', columnGap: 0.5, rowGap: 0.5 }}
                      value={(() => {
                        const h = selectedTemplate?.components?.find((c) => c.type === 'HEADER');
                        if (!h) return 'none';
                        if (h.format === 'TEXT') return 'text';
                        if (h.format === 'IMAGE' || h.format === 'VIDEO' || h.format === 'DOCUMENT') {
                          return h.format;
                        }
                        /* LOCATION / אחר: אין אופציית רדיו — undefined = אף רדיו לא מסומן */
                        return undefined;
                      })()}
                      onChange={(e) => {
                        if (isAuthTemplate) return;
                        const value = e.target.value;
                        const hasHeader = selectedTemplate?.components?.some((c) => c.type === 'HEADER');
                        const header = selectedTemplate?.components?.find((c) => c.type === 'HEADER');

                        if (value === 'none') {
                          handleTemplateComponents({ target: { checked: false } } as any, 'HEADER');
                          return;
                        }

                        if (value === 'text') {
                          if (!hasHeader) {
                            handleTemplateComponents({ target: { checked: true } } as any, 'HEADER');
                          }
                          handleTemplateHeaderType('text');
                          return;
                        }

                        if (value === 'IMAGE' || value === 'VIDEO' || value === 'DOCUMENT') {
                          const fmt = value as WhatsappTemplateHeaderFormat;
                          if (hasHeader && header?.format === fmt) {
                            return;
                          }
                          if (!hasHeader) {
                            handleTemplateComponents({ target: { checked: true } } as any, 'HEADER');
                            handleTemplateHeaderType('media');
                            if (fmt !== 'IMAGE') {
                              queueMicrotask(() => handleTemplateHeaderMediaType(fmt));
                            }
                            return;
                          }
                          if (header?.format === 'TEXT') {
                            handleTemplateHeaderType('media');
                            if (fmt !== 'IMAGE') {
                              queueMicrotask(() => handleTemplateHeaderMediaType(fmt));
                            }
                            return;
                          }
                          handleTemplateHeaderMediaType(fmt);
                        }
                      }}
                    >
                      <FormControlLabel
                        value="none"
                        control={<Radio />}
                        label="ללא כותרת"
                        disabled={isAuthTemplate}
                      />
                      <FormControlLabel
                        value="text"
                        control={<Radio />}
                        label="טקסט"
                        disabled={isAuthTemplate}
                      />
                      <FormControlLabel
                        value="IMAGE"
                        control={<Radio />}
                        label={localization.header.typeMedia.image.label}
                        disabled={isAuthTemplate}
                      />
                      <FormControlLabel
                        value="VIDEO"
                        control={<Radio />}
                        label={localization.header.typeMedia.video.label}
                        disabled={isAuthTemplate}
                      />
                      <FormControlLabel
                        value="DOCUMENT"
                        control={<Radio />}
                        label={localization.header.typeMedia.document.label}
                        disabled={isAuthTemplate}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Template header */}
              {selectedTemplate?.components?.some((component) => component.type === 'HEADER') && (
                <>
                  {createTemplateErrors.headerMedia && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {errorTexts.headerMediaRequired}
                    </Alert>
                  )}
                  <Grid
                    container
                    spacing={1.25}
                    className="new-template-component-header"
                    sx={{ mt: 1.5 }}
                  >
                    <Grid size={12}>
                      {createNewTemplateFormStates.headerType === 'text' && (
                        <Grid container spacing={1.25}>
                          <Grid size={12}>
                            <Grid size={{ md: 8, xs: 12 }}>
                              <TextField
                                onChange={handleTemplateHeaderText}
                                value={
                                  selectedTemplate.components.find((item) => item.type === 'HEADER')?.text ||
                                  ''
                                }
                                ref={headerTextRef}
                                label={localization.header.typeText.title}
                                fullWidth
                                disabled={isAuthTemplate}
                                error={createTemplateErrors.headerText}
                                helperText={createTemplateErrors.headerText ? errorTexts.textInput : null}
                              />
                            </Grid>
                          </Grid>
                          {headerVariable && (
                            <Grid size={12}>
                              <Grid size={{ md: 8, xs: 12 }}>
                                <TextField
                                  onChange={handleHeaderVariableDefaultValue}
                                  value={
                                    selectedTemplate.components.find((item) => item.type === 'HEADER')
                                      ?.example?.header_text?.[0] || ''
                                  }
                                  label={localization.header.typeText.defaultVariableValueTitle}
                                  fullWidth
                                  disabled={isAuthTemplate}
                                  error={createTemplateErrors.headerTextParam}
                                  helperText={createTemplateErrors.headerTextParam ? errorTexts.param : null}
                                />
                              </Grid>
                            </Grid>
                          )}
                          <Grid size={12}>
                            <Button
                              onClick={addNewTextVariableHeader}
                              sx={{
                                alignItems: 'center',
                                gap: 1,
                                '& .MuiSvgIcon-root': {
                                  transition: 'transform 0.3s ease 0s',
                                },
                                '&:hover .MuiSvgIcon-root': {
                                  transform: 'rotate(90deg)',
                                },
                              }}
                              disabled={
                                varsRegular.test(
                                  selectedTemplate.components.find((item) => item.type === 'HEADER')?.text ||
                                    '',
                                ) || isAuthTemplate
                              }
                            >
                              <AddOutlined />
                              {localization.header.typeText.addVariableText}
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                      {createNewTemplateFormStates.headerType === 'media' && (
                        <Grid container spacing={2} alignItems="center">
                          <Grid size="auto">
                            {selectedTemplate?.components?.find((component) => component.type === 'HEADER')
                              ?.format !== 'LOCATION' && (
                              <Tooltip
                                title={
                                  <Box
                                    dangerouslySetInnerHTML={{
                                      __html: selectedTemplate?.components?.find(
                                        (component) => component.type === 'HEADER',
                                      )?.format
                                        ? mediaHeaderTooltips[
                                            selectedTemplate?.components?.find(
                                              (component) => component.type === 'HEADER',
                                            )?.format as WhatsappTemplateHeaderFormat
                                          ] || ''
                                        : '',
                                    }}
                                  />
                                }
                              >
                                <InfoOutlined
                                  sx={{
                                    display: 'block',
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Grid>
                          <Grid size="auto">
                            {selectedTemplate?.components?.find(
                              (component) => component.type === 'HEADER',
                            )?.format === 'IMAGE' && (
                              <Button
                                component="label"
                                role={undefined}
                                variant="contained"
                                tabIndex={-1}
                                disabled={isAuthTemplate}
                                startIcon={<AddPhotoAlternateOutlined />}
                              >
                                {localization.header.typeMedia.image.uploadText}
                                <VisuallyHiddenInput
                                  type="file"
                                  accept="image/jpeg, image/jpg, image/png"
                                  onChange={uploadMediaCreateTemplate}
                                />
                              </Button>
                            )}
                            {selectedTemplate?.components?.find(
                              (component) => component.type === 'HEADER',
                            )?.format === 'VIDEO' && (
                              <Button
                                component="label"
                                role={undefined}
                                variant="contained"
                                tabIndex={-1}
                                disabled={isAuthTemplate}
                                startIcon={<VideoFileOutlined />}
                              >
                                {localization.header.typeMedia.video.uploadText}
                                <VisuallyHiddenInput
                                  type="file"
                                  accept="video/mp4"
                                  onChange={uploadMediaCreateTemplate}
                                />
                              </Button>
                            )}
                            {selectedTemplate?.components?.find(
                              (component) => component.type === 'HEADER',
                            )?.format === 'DOCUMENT' && (
                              <Button
                                component="label"
                                role={undefined}
                                variant="contained"
                                tabIndex={-1}
                                disabled={isAuthTemplate}
                                startIcon={<UploadFileOutlined />}
                              >
                                {localization.header.typeMedia.document.uploadText}
                                <VisuallyHiddenInput
                                  type="file"
                                  accept="application/pdf"
                                  onChange={uploadMediaCreateTemplate}
                                />
                              </Button>
                            )}
                          </Grid>
                          {createTemplateErrors.headerMedia && (
                            <Grid size="auto">
                              <Typography variant="body2" color="error">
                                {errorTexts.headerMediaRequired}
                              </Typography>
                            </Grid>
                          )}
                          <Grid size="auto">
                            {uploadedFileName && <Typography variant="body2">{uploadedFileName}</Typography>}
                          </Grid>
                          <Grid size={12}>
                            <DefaultHeaderFileSection
                              templateName={selectedTemplate.name || ''}
                              language={selectedTemplate.language || 'he'}
                              headerFormat={
                                selectedTemplate?.components?.find((component) => component.type === 'HEADER')
                                  ?.format
                              }
                              disabled={isAuthTemplate}
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  <Divider sx={{ mt: 1.5, mb: 3 }} />
                </>
              )}

              {/* Template body */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" component="h3">
                  {localization.body.title}
                </Typography>
              </Box>
              <Grid container spacing={1.25}>
                <Grid size={12}>
                  <Grid size={{ md: 8, xs: 12 }}>
                    <TextField
                      onChange={handleTemplateText}
                      value={selectedTemplate?.components?.find((item) => item.type === 'BODY')?.text || ''}
                      ref={bodyTextRef}
                      multiline
                      rows={5}
                      placeholder={localization.body.title}
                      fullWidth
                      disabled={isAuthTemplate}
                      inputProps={{
                        maxLength: 1024,
                      }}
                      error={bodyVarsError || createTemplateErrors.bodyText}
                      helperText={
                        bodyVarsError
                          ? createTemplateErrors.bodyVarsErrorType === 'only_variables'
                            ? 'גוף ההודעה לא יכול להכיל רק משתנים - יש להוסיף טקסט'
                            : createTemplateErrors.bodyVarsErrorType === 'starts_with_variable'
                              ? 'גוף ההודעה לא יכול להתחיל במשתנה'
                              : createTemplateErrors.bodyVarsErrorType === 'ends_with_variable'
                                ? 'גוף ההודעה לא יכול להסתיים במשתנה'
                                : createTemplateErrors.bodyVarsErrorType === 'too_many_variables'
                                  ? 'מקסימום 10 משתנים מותרים בגוף ההודעה'
                                  : createTemplateErrors.bodyVarsErrorType === 'duplicate_variable'
                                    ? 'כל משתנה חייב להופיע פעם אחת בלבד - נמצא משתנה כפול'
                                    : localization.body.variablesError
                          : createTemplateErrors.bodyText
                            ? errorTexts.textInput
                            : `${(selectedTemplate?.components?.find((item) => item.type === 'BODY')?.text || '').length}/1024 תווים`
                      }
                    />
                  </Grid>
                </Grid>
                {(bodyVariables?.length || 0) > 0 && (
                  <Fragment>
                    <Grid size={12}>
                      <Typography variant="body2">{localization.body.defaultVariablesValuesTitle}</Typography>
                    </Grid>
                    {bodyVariables?.map((variable: any, index: number) => (
                      <Grid size={12} key={`body-var-${index + 1}`}>
                        <Grid size={{ md: 8, xs: 12 }}>
                          <TextField
                            id={`body-variable-${index}`}
                            inputRef={(el) => {
                              bodyVariableRefsRef.current[index] = el;
                            }}
                            onChange={(e) => handleBodyVariableDefaultValue(e, index)}
                            label={variable}
                            fullWidth
                            disabled={isAuthTemplate}
                            error={createTemplateErrors.bodyParams?.includes(index)}
                            helperText={
                              createTemplateErrors.bodyParams?.includes(index) ? errorTexts.param : null
                            }
                          />
                        </Grid>
                      </Grid>
                    ))}
                  </Fragment>
                )}
                <Grid size={12}>
                  <Grid container spacing={1.5}>
                    <Grid size="auto">
                      <Button
                        onClick={() => addNewTextVariableBody()}
                        disabled={bodyVarsError || isAuthTemplate}
                        sx={{
                          alignItems: 'center',
                          gap: 1,
                          '& .MuiSvgIcon-root': {
                            transition: 'transform 0.3s ease 0s',
                          },
                          '&:hover .MuiSvgIcon-root': {
                            transform: 'rotate(90deg)',
                          },
                        }}
                      >
                        <AddOutlined />
                        {localization.body.addVariableText}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Divider sx={{ mt: 2, mb: 2 }} />

              {/* Footer toggle */}
              <Grid container spacing={1.25} alignItems={'center'} sx={{ mb: 1 }}>
                <Grid size={12}>
                  <FormGroup
                    row
                    sx={{
                      gap: 1.25,
                      '& .MuiFormControlLabel-label': {
                        fontSize: 14,
                      },
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          disabled={isAuthTemplate}
                          checked={selectedTemplate?.components?.some(
                            (component) => component.type === 'FOOTER',
                          )}
                          onChange={(e) => {
                            handleTemplateComponents(e, 'FOOTER');
                          }}
                        />
                      }
                      label={localization.additionalComponents.footer}
                      sx={{
                        pl: 1,
                      }}
                    />
                  </FormGroup>
                </Grid>
              </Grid>

              {/* Template footer */}
              {selectedTemplate?.components?.some((component) => component.type === 'FOOTER') && (
                <Fragment>
                  <Grid container spacing={1.25}>
                    <Grid size={12}>
                      <Grid size={{ md: 8, xs: 12 }}>
                        <TextField
                          onChange={handleTemplateFooter}
                          value={
                            selectedTemplate.components.find((item) => item.type === 'FOOTER')?.text || ''
                          }
                          fullWidth
                          disabled={isAuthTemplate}
                          label={localization.footer.title}
                          helperText={createTemplateErrors.footerText ? errorTexts.textInput : null}
                          className="new-template-component-footer"
                          error={createTemplateErrors.footerText}
                        />
                      </Grid>
                    </Grid>
                    {(footerVariables?.length || 0) > 0 && (
                      <Fragment>
                        <Grid size={12}>
                          <Typography variant="body2">דוגמא למשתנים בכותרת תחתונה</Typography>
                        </Grid>
                        {footerVariables?.map((variable: any, index: number) => (
                          <Grid size={12} key={`footer-var-${index + 1}`}>
                            <Grid size={{ md: 8, xs: 12 }}>
                              <TextField
                                onChange={(e) => handleFooterVariableDefaultValue(e, index)}
                                value={
                                  selectedTemplate.components.find((item) => item.type === 'FOOTER')?.example
                                    ?.footer_text?.[0]?.[index] || ''
                                }
                                label={variable}
                                fullWidth
                                disabled={isAuthTemplate}
                                error={createTemplateErrors.footerParams?.includes(index)}
                                helperText={
                                  createTemplateErrors.footerParams?.includes(index) ? errorTexts.param : null
                                }
                              />
                            </Grid>
                          </Grid>
                        ))}
                      </Fragment>
                    )}
                    {/* Footer variables are not allowed according to Meta policy - button disabled */}
                    {/* 
                  <Grid size={12}>
                    <Button
                      onClick={() => {
                        const footerComponent = selectedTemplate.components.find((item) => item.type === 'FOOTER');
                        if (footerComponent) {
                          const currentText = footerComponent.text || '';
                          const nextVarIndex = (currentText.match(/{{(\d+)}}/g) || []).length + 1;
                          const newText = currentText + `{{${nextVarIndex}}}`;
                          
                          setSelectedTemplate((prevObj) => {
                            const componentsArray = [...prevObj.components];
                            let targetComponent = componentsArray.find((item) => item.type === 'FOOTER');
                            if (!targetComponent) return prevObj;
                            
                            // Add example support
                            const variables = newText.match(/{{(\d+)}}/g);
                            const currentFooterText = targetComponent.example?.footer_text?.[0] || [];
                            
                            targetComponent = {
                              ...targetComponent,
                              text: newText,
                              example: variables ? {
                                ...targetComponent.example,
                                footer_text: [[...currentFooterText, '']],
                              } : targetComponent.example,
                            };
                            
                            // Update the component in the array
                            const componentIndex = componentsArray.findIndex((item) => item.type === 'FOOTER');
                            if (componentIndex !== -1) {
                              componentsArray[componentIndex] = targetComponent;
                            }
                            
                            return { ...prevObj, components: [...componentsArray] };
                          });
                        }
                      }}
                  sx={{
                        alignItems: 'center',
                        gap: 1,
                        '& .MuiSvgIcon-root': {
                          transition: 'transform 0.3s ease 0s',
                        },
                        '&:hover .MuiSvgIcon-root': {
                          transform: 'rotate(90deg)',
                    },
                  }}
                >
                      <AddOutlined />
                      הוסף משתנה
                    </Button>
                  </Grid>
                  */}
                  </Grid>
                </Fragment>
              )}

              <Divider sx={{ mt: 2, mb: 2 }} />

              {/* Buttons section */}
              <Grid container spacing={1.25} alignItems={'center'} sx={{ mb: 1 }}>
                <Grid size={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {localization.additionalComponents.buttons}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 12,
                      color: 'text.secondary',
                      display: 'block',
                      mb: 1,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: localization.buttons.type.tooltip,
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      // Add buttons component if it doesn't exist
                      if (!selectedTemplate?.components?.some((component) => component.type === 'BUTTONS')) {
                        handleTemplateComponents({ target: { checked: true } } as any, 'BUTTONS');
                      }
                      setButtonMenuAnchor(e.currentTarget);
                    }}
                    disabled={isMaxAllButtons || isFlowButton || isAuthTemplate}
                  >
                    {currentLanguage === 'he' ? 'הוסף כפתור' : 'Add Button'}
                  </Button>
                  <Menu
                    anchorEl={buttonMenuAnchor}
                    open={Boolean(buttonMenuAnchor)}
                    onClose={() => setButtonMenuAnchor(null)}
                  >
                    <MenuItem
                      onClick={() => {
                        createNewTemplateButton('QUICK_REPLY');
                        setButtonMenuAnchor(null);
                      }}
                    >
                      {localization.buttons.type.quickReply}
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        createNewTemplateButton('URL');
                        setButtonMenuAnchor(null);
                      }}
                      disabled={isMaxURLButtons}
                    >
                      {localization.buttons.type.visitWebsite}
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        createNewTemplateButton('PHONE_NUMBER');
                        setButtonMenuAnchor(null);
                      }}
                      disabled={isPhoneButton}
                    >
                      {localization.buttons.type.phoneNumber}
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        createNewTemplateButton('FLOW');
                        setButtonMenuAnchor(null);
                      }}
                      disabled={isFlowButton}
                    >
                      {localization.buttons.type.flow}
                    </MenuItem>
                  </Menu>

                  {/* Desktop compatibility warning - moved below Add Button */}
                  {hasDesktopCompatibilityIssue && (
                    <Alert
                      severity="warning"
                      icon={<InfoOutlined />}
                      sx={{
                        mt: 2,
                        fontSize: '0.875rem',
                      }}
                    >
                      {localization.buttons.desktopCompatibilityWarning}
                    </Alert>
                  )}
                </Grid>
              </Grid>

              {/* Template buttons */}
              {selectedTemplate?.components?.some((component) => component.type === 'BUTTONS') && (
                <Fragment>
                  <Grid container spacing={2.5} className="new-template-component-buttons">
                    {/* //TODO Btns typization */}
                    {buttonsList?.map((btn: any, index: number) => (
                      <Grid size={12} key={btn.id ?? index}>
                        <Box
                          ref={
                            marketingOptOutSpotlightButtonId === btn.id
                              ? marketingOptOutSpotlightRowRef
                              : undefined
                          }
                          sx={{ width: '100%' }}
                        >
                          <Box
                            sx={{
                              borderRadius: 2,
                              px: 1,
                              py: 0.5,
                              position: 'relative',
                              width: '100%',
                              boxSizing: 'border-box',
                            }}
                          >
                            <Grid
                              container
                              spacing={2}
                              className="marketing-opt-out-button-row-grid"
                              sx={{ width: '100%' }}
                            >
                            {btn.type === 'QUICK_REPLY' && (
                              <>
                                <Grid size={{ md: 8, xs: 12 }} sx={{ minWidth: 0, maxWidth: '100%' }}>
                                  <Box
                                    data-marketing-opt-out-field={
                                      marketingOptOutTrackedButtonId === btn.id ? 'true' : undefined
                                    }
                                    sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
                                  >
                                    <TextField
                                      onChange={(e) => handleThisButtonText(e, index)}
                                      value={
                                        selectedTemplate?.components?.find((item) => item.type === 'BUTTONS')
                                          ?.buttons[index]?.text || ''
                                      }
                                      error={
                                        buttonsList.some(
                                          (otherBtn: any, otherBtnIndex: number) =>
                                            otherBtn.text === btn.text &&
                                            otherBtnIndex !== index &&
                                            btn.text !== '',
                                        ) ||
                                        createTemplateErrors.buttons?.some((emptyBtn) => {
                                          console.log(`Checking button ${index} against error:`, emptyBtn);
                                          return (
                                            emptyBtn.buttonIndex === index && emptyBtn.fieldTypes.includes('text')
                                          );
                                        })
                                      }
                                      helperText={
                                        buttonsList.some(
                                          (otherBtn: any, otherBtnIndex: number) =>
                                            otherBtn.text === btn.text &&
                                            otherBtnIndex !== index &&
                                            btn.text !== '',
                                        )
                                          ? localization.buttons.uniqueText
                                          : createTemplateErrors.buttons?.some(
                                                (emptyBtn) =>
                                                  emptyBtn.buttonIndex === index &&
                                                  emptyBtn.fieldTypes.includes('text'),
                                              )
                                            ? errorTexts.textInput
                                            : null
                                      }
                                      fullWidth
                                      label={localization.buttons.quickReply.buttonTextTitle}
                                    />
                                  </Box>
                                </Grid>
                                {selectedTemplate?.category === 'MARKETING' &&
                                  marketingOptOutTrackedButtonId === btn.id && (
                                    <Grid size="auto" sx={{ display: 'flex', alignItems: 'flex-start', pt: 1 }}>
                                      <MarketingOptOutInfoButton />
                                    </Grid>
                                  )}
                              </>
                            )}
                          {btn.type === 'URL' && (
                            <Fragment>
                              <Grid size={{ md: 8, xs: 12 }}>
                                <Grid container spacing={2}>
                                  <Grid size={{ md: 6, xs: 12 }}>
                                    <TextField
                                      onChange={(e) => handleThisButtonText(e, index)}
                                      value={btn.text || ''}
                                      fullWidth
                                      label={localization.buttons.visitWebsite.buttonTextTitle}
                                      error={
                                        buttonsList.some(
                                          (otherBtn: any, otherBtnIndex: number) =>
                                            otherBtn.text === btn.text &&
                                            otherBtnIndex !== index &&
                                            btn.text !== '',
                                        ) ||
                                        createTemplateErrors.buttons?.some(
                                          (emptyBtn) => emptyBtn.buttonIndex === index,
                                        )
                                      }
                                      helperText={
                                        buttonsList.some(
                                          (otherBtn: any, otherBtnIndex: number) =>
                                            otherBtn.text === btn.text &&
                                            otherBtnIndex !== index &&
                                            btn.text !== '',
                                        )
                                          ? localization.buttons.uniqueText
                                          : createTemplateErrors.buttons?.some(
                                                (emptyBtn) =>
                                                  emptyBtn.buttonIndex === index &&
                                                  emptyBtn.fieldTypes.includes('text'),
                                              )
                                            ? errorTexts.textInput
                                            : null
                                      }
                                    />
                                  </Grid>
                                  <Grid size={{ md: 6, xs: 12 }}>
                                    <TextField
                                      onChange={(e) => handleThisButtonParamEvent(e, index, 'url')}
                                      value={btn.url || ''}
                                      fullWidth
                                      label={localization.buttons.visitWebsite.urlTitle}
                                      error={createTemplateErrors.buttons?.some(
                                        (emptyBtn) =>
                                          emptyBtn.buttonIndex === index &&
                                          (emptyBtn.fieldTypes.includes('url') ||
                                            emptyBtn.fieldTypes.includes('url_invalid')),
                                      )}
                                      helperText={
                                        createTemplateErrors.buttons?.some(
                                          (emptyBtn) =>
                                            emptyBtn.buttonIndex === index &&
                                            emptyBtn.fieldTypes.includes('url_invalid'),
                                        )
                                          ? 'יש להזין URL תקין (לדוגמה: https://example.com/{{1}})'
                                          : createTemplateErrors.buttons?.some(
                                                (emptyBtn) =>
                                                  emptyBtn.buttonIndex === index &&
                                                  emptyBtn.fieldTypes.includes('url'),
                                              )
                                            ? 'שדה חובה'
                                            : null
                                      }
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid size={{ md: 8, xs: 12 }}>
                                <Grid container spacing={1} flexDirection={'column'}>
                                  <Grid width={'100%'}>
                                    <Button
                                      onClick={(e) => addNewTextURLButton(index, btn.url || '')}
                                      sx={{
                                        alignItems: 'center',
                                        gap: 1,
                                        '& .MuiSvgIcon-root': {
                                          transition: 'transform 0.3s ease 0s',
                                        },
                                        '&:hover .MuiSvgIcon-root': {
                                          transform: 'rotate(90deg)',
                                        },
                                      }}
                                      disabled={varEndRegular.test(btn.url || '')}
                                    >
                                      <AddOutlined />
                                      {localization.body.addVariableText}
                                    </Button>
                                  </Grid>
                                  {varEndRegular.test(btn.url || '') && (
                                    <Grid width={'100%'}>
                                      <TextField
                                        fullWidth
                                        label={`{{1}}`}
                                        value={btn.example?.[0]}
                                        onChange={(e) => handleThisButtonParamExample(e, index)}
                                      />
                                    </Grid>
                                  )}
                                </Grid>
                              </Grid>
                            </Fragment>
                          )}
                          {btn.type === 'PHONE_NUMBER' && (
                            <Grid size={{ md: 8, xs: 12 }}>
                              <Grid container spacing={2}>
                                <Grid size={{ md: 6, xs: 12 }}>
                                  <TextField
                                    onChange={(e) => handleThisButtonText(e, index)}
                                    value={
                                      selectedTemplate?.components?.find((item) => item.type === 'BUTTONS')
                                        ?.buttons[index]?.text || ''
                                    }
                                    fullWidth
                                    label={localization.buttons.phoneNumber.buttonTextTitle}
                                    error={
                                      buttonsList.some(
                                        (otherBtn: any, otherBtnIndex: number) =>
                                          otherBtn.text === btn.text &&
                                          otherBtnIndex !== index &&
                                          btn.text !== '',
                                      ) ||
                                      createTemplateErrors.buttons?.some(
                                        (emptyBtn) =>
                                          emptyBtn.buttonIndex === index &&
                                          emptyBtn.fieldTypes.includes('text'),
                                      )
                                    }
                                    helperText={
                                      buttonsList.some(
                                        (otherBtn: any, otherBtnIndex: number) =>
                                          otherBtn.text === btn.text &&
                                          otherBtnIndex !== index &&
                                          btn.text !== '',
                                      )
                                        ? localization.buttons.uniqueText
                                        : createTemplateErrors.buttons?.some(
                                              (emptyBtn) =>
                                                emptyBtn.buttonIndex === index &&
                                                emptyBtn.fieldTypes.includes('text'),
                                            )
                                          ? errorTexts.textInput
                                          : null
                                    }
                                  />
                                </Grid>
                                <Grid size={{ md: 6, xs: 12 }}>
                                  <TextField
                                    onChange={(e) => handleThisButtonParamEvent(e, index, 'phone_number')}
                                    value={
                                      selectedTemplate?.components?.find((item) => item.type === 'BUTTONS')
                                        ?.buttons[index]['phone_number'] || ''
                                    }
                                    fullWidth
                                    label={localization.buttons.phoneNumber.phoneNumberTitle}
                                    error={createTemplateErrors.buttons?.some(
                                      (emptyBtn) =>
                                        emptyBtn.buttonIndex === index &&
                                        emptyBtn.fieldTypes.includes('phone_number'),
                                    )}
                                    helperText={
                                      createTemplateErrors.buttons?.some(
                                        (emptyBtn) =>
                                          emptyBtn.buttonIndex === index &&
                                          emptyBtn.fieldTypes.includes('phone_number'),
                                      )
                                        ? errorTexts.textInput
                                        : null
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          )}
                          {btn.type === 'FLOW' && (
                            <Grid size={{ md: 8, xs: 12 }}>
                              <Box
                                sx={{
                                  py: 1.5,
                                  borderTop: '1px solid #e7e7e7',
                                  borderBottom: '1px solid #e7e7e7',
                                }}
                              >
                                <Box
                                  component={'a'}
                                  href={`https://business.facebook.com/latest/whatsapp_manager/flows/?nav_ref=whatsapp_manager&is_from_mbs=1&business_id=${businessAccountID}`}
                                  target="_blank"
                                  mb={1}
                                  display={'inline-block'}
                                >
                                  {localization.buttons.flowBtn.toCreateFlow}
                                </Box>
                                <Grid container spacing={1}>
                                  <Grid size={{ md: 4, xs: 12 }}>
                                    <TextField
                                      onChange={(e) => handleThisButtonText(e, index)}
                                      value={btn?.text || ''}
                                      fullWidth
                                      label={localization.buttons.flowBtn.textLabel}
                                      error={buttonsList.some(
                                        (otherBtn: any, otherBtnIndex: number) =>
                                          otherBtn.text === btn.text &&
                                          otherBtnIndex !== index &&
                                          btn.text !== '',
                                      )}
                                      helperText={
                                        buttonsList.some(
                                          (otherBtn: any, otherBtnIndex: number) =>
                                            otherBtn.text === btn.text &&
                                            otherBtnIndex !== index &&
                                            btn.text !== '',
                                        )
                                          ? localization.buttons.uniqueText
                                          : null
                                      }
                                    />
                                  </Grid>
                                  <Grid size={{ md: 4, xs: 12 }}>
                                    <FormControl fullWidth>
                                      <InputLabel id="flow-btn-action-label">
                                        {localization.buttons.flowBtn.action.label}
                                      </InputLabel>
                                      <Select
                                        labelId="flow-btn-action-label"
                                        id="flow-btn-action"
                                        value={btn.flow_action}
                                        label="Action"
                                        // onChange={handleChange}
                                      >
                                        <MenuItem value={'navigate'}>
                                          {localization.buttons.flowBtn.action.navigate}
                                        </MenuItem>
                                        <MenuItem value={'data_exchange'}>
                                          {localization.buttons.flowBtn.action.dataExchange}
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid size={{ md: 4, xs: 12 }}>
                                    <FormControl>
                                      <RadioGroup
                                        row
                                        name="row-radio-buttons-flow-type"
                                        value={createNewTemplateFormStates.flowType}
                                        onChange={(e) => handleFlowBtnType(e, index)}
                                      >
                                        <FormControlLabel
                                          value="flow_id"
                                          control={<Radio />}
                                          label={localization.buttons.flowBtn.action.flowIdRadio}
                                        />
                                        <FormControlLabel
                                          value="flow_json"
                                          control={<Radio />}
                                          label={localization.buttons.flowBtn.action.flowJsonRadio}
                                        />
                                      </RadioGroup>
                                    </FormControl>
                                  </Grid>
                                  <Grid size={{ md: 8, xs: 12 }}>
                                    {createNewTemplateFormStates.flowType === 'flow_id' && (
                                      <TextField
                                        onChange={(e) => handleThisButtonParamEvent(e, index, 'flow_id')}
                                        value={btn?.flow_id || ''}
                                        fullWidth
                                        label={localization.buttons.flowBtn.action.flowIdLabel}
                                      />
                                    )}
                                    {createNewTemplateFormStates.flowType === 'flow_json' && (
                                      <TextField
                                        onChange={(e) => handleThisButtonParamEvent(e, index, 'flow_json')}
                                        multiline
                                        rows={5}
                                        value={btn?.flow_json || ''}
                                        fullWidth
                                        label={localization.buttons.flowBtn.action.flowJsonLabel}
                                        dir="ltr"
                                      />
                                    )}
                                  </Grid>
                                  <Grid size={12}>
                                    <Grid size={{ md: 8, xs: 12 }}>
                                      <TextField
                                        onChange={(e) =>
                                          handleThisButtonParamEvent(e, index, 'navigate_screen')
                                        }
                                        value={btn?.navigate_screen || ''}
                                        fullWidth
                                        label={localization.buttons.flowBtn.action.navScreen}
                                        // error={buttonsList.some(
                                        //   (otherBtn, otherBtnIndex) =>
                                        //     otherBtn.text === btn.text &&
                                        //     otherBtnIndex !== index &&
                                        //     btn.text !== "",
                                        // )}
                                        // helperText={
                                        //   buttonsList.some(
                                        //     (otherBtn, otherBtnIndex) =>
                                        //       otherBtn.text === btn.text &&
                                        //       otherBtnIndex !== index &&
                                        //       btn.text !== "",
                                        //   )
                                        //     ? localization.buttons.uniqueText
                                        //     : null
                                        // }
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Grid>
                          )}

                          <Grid size="auto" onClick={() => deleteThisButton(index)}>
                            <IconButton
                              color="error"
                              sx={{
                                mt: btn.type === 'FLOW' ? 6 : 1,
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Grid>
                        </Grid>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Fragment>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Template Preview Sidebar */}
        <RightAppSidebarContent id="templates-create-preview-sidebar">
          <TemplatePreviewSidebar selectedTemplate={selectedTemplate} />
        </RightAppSidebarContent>

        {/* AI Helper Sidebar (Legacy server chat) */}
        <RightAppSidebarContent id="template-ai-helper">
          <TemplateAIHelper onAssistantMessageComplete={handleLegacyAiAssistantComplete} />
        </RightAppSidebarContent>

        <SmartTemplateBriefDialog
          open={smartBriefOpen}
          onClose={() => !smartGenerating && setSmartBriefOpen(false)}
          onSubmitBrief={handleSmartBriefSubmit}
          loading={smartGenerating}
        />

        <TemplateAIGeneratedPreviewDialog
          open={aiPreviewOpen}
          onClose={() => {
            setAiPreviewOpen(false);
            setPreviewSmartPayload(null);
            setPreviewLegacyText('');
          }}
          onConfirm={handleAiPreviewConfirm}
          mode={aiPreviewMode}
          smartPayload={previewSmartPayload}
          legacyText={previewLegacyText}
        />

        <MarketingOptOutSpotlightOverlay
          open={Boolean(marketingOptOutSpotlightButtonId && marketingOptOutSpotlightRect)}
          rect={marketingOptOutSpotlightRect}
          onDismiss={dismissMarketingOptOutSpotlight}
        />
            </MainCard>
          </Box>
        </Box>
      ) : (
        <>
          <Breadcrumbs
            custom
            heading={'תבנית חדשה'}
            links={[
              { title: 'Home', to: DASHBOARD_PATH, icon: BreadCrumbsHomeIcon, onlyIcon: true },
              { title: 'תבניות', to: '/templates/list' },
              { title: 'יצירת תבנית' },
            ]}
            sx={{ mb: '24px' }}
          />

          {/* AI Helper Sidebar (Legacy) */}
          <RightAppSidebarContent id="template-ai-helper">
            <TemplateAIHelper onAssistantMessageComplete={handleLegacyAiAssistantComplete} />
          </RightAppSidebarContent>
        </>
      )}
    </ViewRevealer>
  );
}

export default CreateEditTemplate;
